'use client'
/**
 * PATTERN 8: ATLAS. The living map — every listing on the market as a point on
 * Central Oregon, every place as a touchable silhouette, a sales-heat wash
 * from closings, pulses on real events, one price scrubber, one row of type
 * toggles, and a card that answers "what is here" on hover or tap. The
 * homepage Stage (owned flyover) sits above it; scoped to a boundary, the
 * living map of a city, a neighborhood, a community, a plat.
 *
 * Why it exists (Matt 2026-09-01): "a buyer does not want every home in
 * Central Oregon" — a buyer wants THEIR place, price, and type, and the site's
 * moat is that it knows every one of them. "Look alive like real activity
 * happening."
 *
 * Honest by construction (section 0): every number the Atlas prints is a
 * count or a median of the dots on screen — the listings the caller passed,
 * filtered by the visitor's own price and type choices, sold dots included
 * in the same filter. One population, one source, named in the source line.
 * A caller whose read came back short says so through `incomplete`, and the
 * Atlas then prints NO counts: a partial count is a wrong number.
 *
 * The map is inventory as marks, sales as a field. Closings in the heat
 * window paint a navy-on-cream kernel in this map's projection; active and
 * pending stay as dots on top and stay clickable. Sold dots are omitted as
 * marks so the field is not double-encoded. Quiet and lot maps draw no heat.
 * Incomplete reads print no heat, same as no counts. Pending and active
 * never count as heat.
 *
 * LAYERS:
 *   svg      silhouettes, the sales wash, and the inventory dots. Each place
 *            is drawn twice: a cream halo under a navy line, so the outline
 *            reads over the field. Dots are zero-length stroked paths with
 *            non-scaling-stroke, so their diameter is screen pixels at any
 *            scale — the server-rendered map is the final map.
 *   html     SITE-127 price pills (735K / $1.5M) on for-sale and pending
 *            marks, plus the home blow-up (photo + ask + street) on hover.
 *            SITE-128 residual: overlapping pills collapse to count bubbles
 *            that expand on zoom (city folds); spaced neighborhood pins stay.
 *   html     town labels, positioned from the measured view, in real type.
 *   svg      SITE-128 amenity layers: recorded park polygons + trail lines
 *            under the homes (navy on cream, never a Google blue tile).
 *   svg      the pulses, alone, on their own layer, capped with slots for
 *            each kind of event so a close always shows, paused off-screen.
 *   html     the card and the dock. The dock is a legend in flow — under
 *            the map on a phone, under the head in the desktop column — so
 *            the map keeps its full height.
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import {
  bboxOfRings,
  labelAnchor,
  lineStringParts,
  outerRings,
  pointsToPath,
  polygonInteriorPoint,
  ringsToPath,
  type LonLat,
  type Ring,
} from '@/lib/geo/project-svg'
import { decodeBasemapFeature, type Basemap, type BasemapFeature } from '@/lib/geo/basemap'
import { atlasBasemapHref } from '@/lib/atlas/atlas-basemap-href'
import { atlasTaxlotsHref, type AtlasTaxlotsScope } from '@/lib/atlas/atlas-taxlots-href'
// A client-safe import: getTaxlots.ts (the module this constant lives beside
// for server callers) chains into lib/supabase/server.ts, which Next.js
// refuses to bundle into a 'use client' file.
import { TAXLOT_DISCLAIMER } from '@/lib/data/geo/taxlot-disclaimer'
import {
  ATLAS_CAM_HOME,
  ATLAS_K_MAX,
  ATLAS_K_MIN,
  fitRect,
  panBy,
  publishAtlasView,
  screenToWorld,
  visibleLonLat,
  zoomAt,
  type AtlasCam,
  type AtlasViewBounds,
} from '@/lib/geo/atlas-camera'
import { placeDoorLabels, shortPlaceLabel } from '@/lib/place/short-place-label'
import {
  childPaintLive,
  childSelectionId,
  childSelectionMatches,
  hierarchyChildIdSet,
} from '@/lib/place/map-hierarchy'
import { publishPlatDisplayName } from '@/lib/market/publish-plat-display-name'
import { atlasDoorIndices, atlasNextDoor, atlasPlaceDoors } from '@/lib/atlas/atlas-doors'

export type { AtlasViewBounds }
import {
  ATLAS_HEAT_WINDOW_DAYS,
  atlasHeatWindowLabel,
  isAtlasHeatClosing,
  isAtlasPulseSold,
  salesHeatField,
} from '@/lib/atlas/sales-heat'
import { atlasLabelBox, packAtlasLabels, type AtlasLabelCandidate } from '@/lib/atlas/pack-labels'
import {
  ATLAS_CLUSTER_PIN_LABEL,
  atlasClusterAskSpan,
  atlasClusterMedianAsk,
  atlasPinShouldPaint,
  formatAtlasClusterRange,
  formatAtlasPinPrice,
} from '@/lib/atlas/pin-price'
import {
  atlasBeyond,
  atlasCounts,
  atlasFrameBox,
  atlasIsClosingsMap,
  atlasMedianScope,
  atlasMembership,
  atlasPriceScale,
  atlasProjection,
  atlasRegionStats,
  type AtlasDotSummary,
  type AtlasPlaceStat,
} from '@/lib/atlas/atlas-derive'
import {
  ATLAS_CLUSTER_PILL,
  ATLAS_PIN_CLUSTER_RADIUS_PX,
  CITY_FOLD_CLUSTER_BREAKPOINT_PX,
  atlasClusterCanExpand,
  atlasClusterSize,
  atlasClusterWorldBounds,
  atlasViewFromStage,
  clampAtlasPinToIsland,
  clusterAtlasPins,
  mergeOverlappingAtlasClusters,
  floorCityFoldPaintView,
  hitAtlasPinLayer,
  pickCityFoldClusterStage,
  projectPinsToFoldStage,
} from '@/lib/atlas/cluster-pins'
import type { AtlasFilterState } from '@/lib/atlas/atlas-filter'
import { forSaleText, mapListingCount, reachChipBasisText } from '@/lib/listing-counts'
import { V3_ROOT_CLASS, type V3Text } from './atoms'
import './tokens.css'
import './V3Atlas.css'

/**
 * How many chips show before the set folds behind one "+ N more" chip
 * (SITE-07 quality pass, 2026-09-09). Eight on a phone is two to three
 * wrapped rows at 375: enough to read as the place's children under the map,
 * short of the wall that eighty plats make. From 48rem the column holds
 * twenty-four before the same chip folds the rest — the first record at 1440
 * showed seventeen rows of chips beside empty cream (canon lens). The width
 * that applies is decided in V3Atlas.css, never by a media query read in
 * render, so the server and the client agree: both rests and both labels
 * are in the markup and the stylesheet shows one of each.
 */
const CHIP_FOLD_AT = 8
const CHIP_FOLD_AT_WIDE = 24

/* -------------------------------------------------------------------------- */
/* Data                                                                        */
/* -------------------------------------------------------------------------- */

/** Steps in the price scrubber's distribution. */
const PRICE_BINS = 24
/** Under this many priced homes the scrubber draws a tick per home, not steps. */
const PRICE_TICKS_UNDER = 40

export type AtlasDot = {
  /** Listing key — the React key. */
  k: string
  /** Canonical listing URL. Absent on a closing with no public page. */
  href?: string
  lat: number
  lng: number
  /** List price (close price for a sold dot) in dollars; null when withheld. */
  p: number | null
  /** Type key: house · condo · townhouse · manufactured · land · multi · commercial · other. */
  t: string
  /** Days since the listing went on market; null when the feed withheld it. */
  age: number | null
  /**
   * active · pending · sold — the dot's own status, for the activity layer.
   * closed: a historical closing (a broker's record), drawn as a dot, counted
   * as a closing, never pulsed.
   */
  s: 'active' | 'pending' | 'sold' | 'closed'
  /** Days since close for a sold dot; null otherwise. */
  soldAgo?: number | null
  /** Hero photo, already sized for the hover blow-up. Absent when withheld. */
  photo?: string | null
  /** "714 Wrangler Court, Sisters" — the card address. */
  street?: string | null
  beds?: number | null
  baths?: number | null
  sqft?: number | null
}

/** A recent, real event: what the live line prints. */
export type AtlasEvent = { key: string; kind: 'new' | 'pending' | 'sold'; label: string; href: string }

export type AtlasRegionKind = 'town' | 'community' | 'neighborhood' | 'subdivision'

export type AtlasRegion = {
  id: string
  /** town = the base silhouette(s); community / neighborhood / subdivision = the places on it. */
  kind: AtlasRegionKind
  /** What the card calls it: "City", "Community", "Neighborhood", "Subdivision". Defaults by kind. */
  kindLabel?: string
  name: string
  href: string
  geometry: GeoJSON.Geometry
}

export type AtlasType = { key: string; label: string }

/** Recorded park / trail geom the place page already fetched. Never invented. */
export type AtlasAmenityPark = {
  id: string
  name: string
  href?: string
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
}
export type AtlasAmenityTrail = {
  id: string
  name: string
  href?: string
  geometry: GeoJSON.LineString | GeoJSON.MultiLineString
}
export type AtlasAmenityLayers = {
  parks?: readonly AtlasAmenityPark[]
  trails?: readonly AtlasAmenityTrail[]
}

/** One lot line. `subject` is the lot the page's own home sits on. */
export type AtlasParcel = {
  id: string
  subject: boolean
  name?: string
  geometry: GeoJSON.Geometry
}


export type V3AtlasProps = {
  id: string
  /** The page H1 (D11 lock on the homepage), or the section title. */
  headline: V3Text
  headingLevel?: 1 | 2
  /**
   * How the headline is SET, independent of the level it renders at.
   *
   * `display` (the default, and what every caller before 2026-09-09 gets) is
   * the Amboqia display line the homepage and the place pages open with.
   *
   * `eyebrow` is for the one composition where that is wrong: the Atlas opens
   * DIRECTLY under the page's own H1, so a second display line repeats the
   * title in the same face at the same size and the top of the page says the
   * same thing twice ("Single-family in Bend" then "Single-family on the map"
   * — taste table 2026-09-08, place-type, the passage the evaluator called the
   * dullest on the page). The heading stays a real heading for the outline and
   * for aria-labelledby; only its setting changes to a small tracked label.
   */
  headlineTone?: 'display' | 'eyebrow'
  /**
   * Opt-in sentence that replaces the generated claim. Closings maps otherwise
   * print "N closings." which reads as a KPI tile (SITE-74). Absent, the
   * generated claim is unchanged for every existing caller.
   */
  claimText?: string
  /**
   * OPT-IN (SITE-47). Whether the Atlas prints a CLAIM SENTENCE above the map
   * on a live-inventory map.
   *
   * `none` is the default and what every caller before 2026-09-09 renders: an
   * inventory map's claim is the empty string, because PLACE_PAGES §4 killed
   * the old triple-count line (every type for sale, pending, sold in the last
   * 30 days) and its trailing how-to-zoom instruction that used to sit there.
   * That kill was right about the how-to and the triple count; it left the
   * section opening on a bare label, which the taste table of 2026-09-08
   * scored as a first read that requires a hover to discover what the marks
   * mean.
   *
   * `inventory` restores ONE clause, built from the SAME filtered counts the
   * marks are drawn from — so it moves with the type toggles and the price
   * scrubber instead of describing a set the reader has already changed — and
   * it names its own feed from the `source` trace. No pending count (the key
   * carries that beside its own mark) and no zoom instruction.
   */
  claimTone?: 'none' | 'inventory'
  /**
   * OPT-IN (SITE-47). Where the dot KEY sits.
   *
   * `dock` is the default and what every caller before 2026-09-09 renders: in
   * the dock, which is a left column beside the map at 64rem but drops BELOW
   * the map on a phone and on a wide boundary (`.v3-atlas.is-wide`, the plat
   * case). On those the encoding is not on screen with the map it explains.
   *
   * `head` moves the same list into the head, under the claim and above the
   * map, in every layout. The key is rendered once either way.
   */
  keyPlacement?: 'dock' | 'head'
  /**
   * The population, inline. A place page passes `[]` here and names the
   * population by `dotsSrc` instead (UXLIVE-3, 2026-09-23): 5,650 inline dots
   * were 2.0 MB of /about's RSC payload.
   */
  dots: readonly AtlasDot[]
  /**
   * UXLIVE-3. Where the dots load from after paint (app/api/atlas/dots), when
   * `dots` is empty. Built on the server by lib/atlas/atlas-deferred.ts from
   * the scope the page read, so the fetched population is the one the page's
   * counts were computed from.
   */
  dotsSrc?: string | null
  /**
   * UXLIVE-3. The figures the Atlas prints at rest, computed on the server
   * from the full population with the same functions this component runs
   * (lib/atlas/atlas-derive.ts): the frame box, the counts, each place's count
   * and median, the scrubber range. Until the dots arrive these are what the
   * key, the chips, the cards and the source line print, so the server HTML
   * carries every count; the type toggles and the scrubber wait for the dots,
   * because a filtered count needs them.
   */
  dotsSummary?: AtlasDotSummary | null
  regions: readonly AtlasRegion[]
  /**
   * SITE-128 #2: child plats for select → zoom. Not the default highlight.
   * Neighborhood / community pass recorded GIS children here so Old Bend
   * does not paint twenty plats as the place.
   */
  childRegions?: readonly AtlasRegion[]
  /**
   * Subdivision grain: tight frame pad + readable subject stroke so a plat
   * like DRW fills the stage instead of sitting faint and tiny.
   */
  subjectGrain?: boolean
  /** Type toggles, in display order. Keys match AtlasDot.t. */
  types: readonly AtlasType[]
  /** The section 0 trace: what the dots are and where they come from. */
  source?: string
  /**
   * The FEED's short name, for the attribution clause on a `claimTone`
   * `inventory` claim ("…, live MLS through Oregon Data Share."). Passed by the
   * caller and never derived: this component's `source` traces are written
   * population-first ("Every active and pending listing … through Oregon Data
   * Share inside the recorded boundary of Park Addition."), so V3SourceLine's
   * leading-segment rule folds them to the whole sentence and the claim came
   * out ungrammatical. Absent, the claim states the count and stops — the full
   * trace is still under the map in the source disclosure either way.
   */
  sourceName?: string
  /** "Sep 1, 2026, 9:40 PM" — when the dots were read, already formatted. */
  stamp?: string
  /** The caller's read came back short: the Atlas prints no counts. */
  incomplete?: boolean
  /** The newest real events, already formatted, for the live line. */
  events?: readonly AtlasEvent[]
  /** The search control, rendered in the aside under the live line. */
  /**
   * The word for one dot: "closing" / "closings" on a broker's record map.
   * Absent, the map speaks of listings (or the single type on).
   */
  noun?: { one: string; many: string }
  /**
   * What the frame fits: every region (the default), or the dots' own extent
   * — a broker's closings sit in two towns and should fill the frame, not a
   * corner of the whole region. Regions outside the frame are clipped.
   */
  fit?: 'regions' | 'dots'
  /**
   * One dot held above the rest and named — the home a listing page is about.
   * Matched by the dot's key; absent from the dots, nothing is drawn.
   */
  highlight?: { key: string; label: string }
  /**
   * How many places with a recorded boundary the frame holds when the
   * outlines are a subset (a plat cap): the source line says "N of M".
   */
  outlinedOf?: number
  /**
   * The roads, rivers and lakes drawn under everything else — context, so a
   * dot has a place rather than floating in cream. Clipped and thinned to the
   * frame by the page (lib/geo/basemap-source.ts); absent, the map draws none.
   */
  basemap?: Basemap | null
  /**
   * UXLIVE-3. Where the same clipped basemap loads from after paint
   * (app/api/atlas/basemap), when `basemap` is absent: static TIGER geometry,
   * cached at the edge and in the browser instead of inlined twice per page.
   */
  basemapSrc?: string | null
  /**
   * Lot lines: the parcel the page is about, and the parcels around it. From
   * the county assessor's cadastral layer, so it is the recorded shape of a
   * lot and NOT a survey — a page drawing these carries the disclaimer beside
   * the map (lib/data/geo/getTaxlots.ts, TAXLOT_DISCLAIMER).
   */
  parcels?: readonly AtlasParcel[]
  /**
   * Frame the map on this geometry and nothing else. Not drawn — it only sets
   * the projection. A lot view needs it: a 0.18-acre parcel on a map framed by
   * its neighborhood renders four pixels across, which is a line nobody can
   * read.
   */
  frame?: GeoJSON.Geometry | null
  /**
   * This map is one object, not a population: no type switches, no price
   * scrubber, no count claim. A lot view has one dot and one polygon, and
   * "1 listing of every type for sale, slide the price" is noise on it.
   */
  quiet?: boolean
  children?: ReactNode
  className?: string
  /**
   * Visible geographic box after pan/zoom. `null` at k===1 (full frame) so
   * the photographed list below stays unfiltered at rest.
   */
  onViewChange?: (bounds: AtlasViewBounds | null) => void
  /**
   * ROW ↔ MARK LINK. The listing key a sibling list is pointing at: the map
   * rings that mark and dims nothing else. One extra mark, so a hover over a
   * list of 120 rows costs one small re-render and not 1,500 dot paths.
   * Absent, nothing is drawn and the map behaves exactly as it did.
   */
  linkedKey?: string | null
  /**
   * The reverse direction: the key of the mark under the pointer, reported up
   * so the sibling list can raise the matching row. Called with `null` when
   * the pointer leaves every mark.
   */
  onLinkedKeyChange?: (key: string | null) => void
  /**
   * SITE-128: collapse overlapping ask pills to count bubbles. Default on —
   * city fold passes it explicitly so the page, not only the primitive, is
   * what Look can grep. Neighborhood / community keep the default; their
   * spacing already leaves singles as pills.
   */
  clusterPins?: boolean
  /** Occupied-cell size in stage pixels. City fold passes the grid constant. */
  clusterCellPx?: number
  /**
   * First-paint + membership stage so pinMarks exist in SSR HTML and desktop
   * clustering does not follow a collapsed ResizeObserver box. City fold only.
   */
  clusterStageHint?: { w: number; h: number }
  /**
   * Phone twin of `clusterStageHint`. City fold passes it so 375 stays on
   * the 14-bubble + 2-pill layout. Absent, the desktop hint is used at every
   * width (neighborhood / community omit both).
   */
  clusterStageHintPhone?: { w: number; h: number }
  /**
   * SITE-128 craft #2: recorded park polygons + trail lines on the homes
   * Atlas. Pages pass only geom we already hold. Missing official geometry
   * stays omitted — this prop never invents a corridor or a circle.
   */
  amenities?: AtlasAmenityLayers | null
  /**
   * SITE-129: community + subdivision inventory lives in V3PlaceInventory.
   * The Atlas price range must not mount on those grains — CSS hide is not
   * enough; the control cannot return in the DOM.
   */
  hidePriceScrubber?: boolean
  /**
   * Community and neighborhood pages: the subdivision list lives beside this
   * map. Selecting one zooms to that shape. The reach list and the claim
   * sentence stay off so the map is not explained twice.
   */
  selectedSubdivisionId?: string | null
  onSubdivisionSelect?: (id: string | null) => void
  /**
   * Paired selection only: the accessible name of each child's full-size
   * control (its rail row), keyed by the row id (childSelectionId of the
   * shape id). A child polygon is a door only when its row is here, and it
   * carries exactly that name, so the small polygon and the row pair as one
   * control (WCAG 2.5.8 Equivalent, ci:tap-targets). PlaceSubdivisionAtlas
   * passes the rail it sits beside; a paired map without it has no doors.
   * See lib/atlas/atlas-doors.ts.
   */
  childDoorNames?: Readonly<Record<string, string>> | null
  /**
   * Matt 2026-09-23: "also focus the maps homes on that neighborhood." The
   * listing keys inside each child boundary — EXACTLY the map
   * childListingKeys (lib/place/place-child-stock.ts) builds the rail's own
   * "56 single-family..." counts from. When a district is selected, a dot
   * whose key is not in that district's set is hidden: the map can never
   * show a different population than the rail line beside it implies.
   * Absent, selection only moves the camera (every caller before this date).
   */
  memberKeysBySlug?: Readonly<Record<string, readonly string[]>> | null
  /**
   * Matt 2026-09-23: "maybe... lots." Which recorded boundary row a rail
   * id's tax-lot lines live under, keyed the same way memberKeysBySlug is.
   * Absent, or the selected id missing from the map: no lot fetch, no lot
   * layer — never invented from the id alone (a Bend neighborhood's own row
   * in public.boundaries is "bend-<slug>", not the bare rail id).
   */
  taxlotBoundaries?: Readonly<Record<string, AtlasTaxlotsScope>>
  /**
   * W1 (Muse audit 2026-10-20). The reader's type toggles and price ceiling,
   * reported whenever they change, so a homes block beside the map applies
   * the same filter (lib/atlas/atlas-filter.ts) and its dial headers move with
   * the key. Absent, the filter stays the map's own.
   */
  onFilterChange?: (filter: AtlasFilterState) => void
  /**
   * W1. The key's "N for sale" names its basis in plain words ("280 for sale,
   * all home types", or the types still on), from mapListingCount
   * (lib/listing-counts.ts). Pages that print another count of the same place
   * (a detached single-family figure) turn it on; absent, the key reads as it
   * did.
   */
  keyBasis?: boolean
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

/** Pulse slots per event kind, so a month of closes always has living marks. */
const PULSE_SLOTS = { new: 16, pending: 6, sold: 18 } as const
const KIND_LABEL: Record<AtlasRegionKind, string> = {
  town: 'Town',
  community: 'Community',
  neighborhood: 'Neighborhood',
  subdivision: 'Subdivision',
}

/**
 * The price a control is actually set to. Two decimals under ten million,
 * because the scrubber steps in twenty-five thousands: one decimal printed
 * "$1.8M" for both $1,775,000 and $1,750,000, so the number on screen was not
 * the number being applied (evaluator round five, LISTING-NOBOUNDARY-3).
 */
function fmtShort(usd: number): string {
  if (usd >= 1_000_000) {
    const m = usd / 1_000_000
    return `$${m >= 10 ? m.toFixed(0) : m.toFixed(2).replace(/\.?0+$/, '')}M`
  }
  return `$${Math.round(usd / 1000)}K`
}

/**
 * UXLIVE-3: what the Atlas fetches after paint. Typed loosely at the edge and
 * checked here, so a malformed body leaves the server's figures in place.
 */
type FetchedDots = { dots: AtlasDot[]; stamp: string | null; complete: boolean }

function asFetchedDots(body: unknown): FetchedDots | null {
  if (!body || typeof body !== 'object') return null
  const rec = body as { dots?: unknown; stamp?: unknown; complete?: unknown }
  if (!Array.isArray(rec.dots)) return null
  return {
    dots: rec.dots as AtlasDot[],
    stamp: typeof rec.stamp === 'string' && rec.stamp ? rec.stamp : null,
    complete: rec.complete !== false,
  }
}

function asFetchedBasemap(body: unknown): Basemap | null {
  if (!body || typeof body !== 'object') return null
  const rec = body as Partial<Basemap>
  if (!Array.isArray(rec.roads) || !Array.isArray(rec.waterways) || !Array.isArray(rec.bodies)) return null
  if (typeof rec.q !== 'number') return null
  return rec as Basemap
}

/**
 * Matt 2026-09-23, requirement 6: the lot layer's fetched body, checked the
 * same defensive way asFetchedBasemap is — a malformed or empty response
 * draws no lots rather than throwing, since the lot layer is decoration on
 * top of a map that must keep working without it.
 */
function asFetchedTaxlots(body: unknown): AtlasParcel[] | null {
  if (!body || typeof body !== 'object') return null
  const rec = body as { lots?: unknown }
  if (!Array.isArray(rec.lots)) return null
  const out: AtlasParcel[] = []
  for (const item of rec.lots) {
    if (!item || typeof item !== 'object') continue
    const row = item as { taxlot?: unknown; geometry?: unknown }
    if (typeof row.taxlot !== 'string' || !row.taxlot) continue
    const geometry = row.geometry
    if (!geometry || typeof geometry !== 'object') continue
    const kind = (geometry as { type?: unknown }).type
    if (kind !== 'Polygon' && kind !== 'MultiPolygon') continue
    out.push({ id: row.taxlot, subject: false, geometry: geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon })
  }
  return out
}

/**
 * Run `start` once the section is within a screen of the viewport AND the
 * main thread has gone idle after hydration: the dots and the basemap never
 * compete with the page's first paint or its hero image (UXLIVE-3).
 */
function whenNearAndIdle(el: Element | null, start: () => void): () => void {
  let done = false
  let idleId: number | null = null
  let timer: number | null = null
  const fire = () => {
    if (done) return
    done = true
    start()
  }
  const schedule = () => {
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      cancelIdleCallback?: (id: number) => void
    }
    if (typeof w.requestIdleCallback === 'function') idleId = w.requestIdleCallback(fire, { timeout: 1500 })
    else timer = window.setTimeout(fire, 200)
  }
  let io: IntersectionObserver | null = null
  if (el && typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io?.disconnect()
          io = null
          schedule()
        }
      },
      { rootMargin: '100% 0px' },
    )
    io.observe(el)
  } else {
    schedule()
  }
  return () => {
    done = true
    io?.disconnect()
    const w = window as Window & { cancelIdleCallback?: (id: number) => void }
    if (idleId != null && typeof w.cancelIdleCallback === 'function') w.cancelIdleCallback(idleId)
    if (timer != null) window.clearTimeout(timer)
  }
}

/** One GET with a single retry; resolves null when both fail or the body is malformed. */
async function fetchJsonOnce<T>(url: string, parse: (body: unknown) => T | null, signal: AbortSignal): Promise<T | null> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const res = await fetch(url, { signal, credentials: 'same-origin' })
      if (res.ok) return parse(await res.json())
      if (res.status >= 400 && res.status < 500) return null
    } catch {
      if (signal.aborted) return null
    }
    if (attempt === 0) await new Promise((r) => window.setTimeout(r, 1500))
  }
  return null
}

/** The noun for a count, honest to the types on screen: lots are not homes. */
const NOUNS: Record<string, readonly [string, string]> = {
  house: ['house', 'houses'],
  condo: ['condo', 'condos'],
  townhouse: ['townhome', 'townhomes'],
  manufactured: ['manufactured home', 'manufactured homes'],
  land: ['lot', 'lots'],
  multi: ['multi-family building', 'multi-family buildings'],
  commercial: ['commercial property', 'commercial properties'],
}
function nounFor(count: number, typesOn: readonly AtlasType[], allTypes: readonly AtlasType[]): string {
  const single = typesOn.length === 1 && typesOn.length !== allTypes.length ? NOUNS[typesOn[0]!.key] : null
  if (single) return count === 1 ? single[0] : single[1]
  return count === 1 ? 'listing' : 'listings'
}

type RegionShape = AtlasRegion & {
  rings: Ring[]
  anchor: readonly [number, number] | null
  area: number
  bbox: ReturnType<typeof bboxOfRings>
}
type PlacedShape = RegionShape & { d: string }
type View = { w: number; h: number; scale: number; ox: number; oy: number }

type PaintedPin =
  | { kind: 'pin'; d: AtlasDot; i: number; x: number; y: number; label: string }
  | {
      kind: 'cluster'
      id: string
      x: number
      y: number
      count: number
      indices: number[]
      minP: number
      maxP: number
      /** The median ask of the members: the pill's face (UXLIVE-6). */
      median: number | null
    }

/* -------------------------------------------------------------------------- */
/* V3Atlas                                                                     */
/* -------------------------------------------------------------------------- */

export function V3Atlas({
  id,
  headline,
  headingLevel = 1,
  headlineTone = 'display',
  claimTone = 'none',
  claimText,
  keyPlacement = 'dock',
  dots: dotsProp,
  dotsSrc,
  dotsSummary,
  regions,
  childRegions = [],
  subjectGrain = false,
  types,
  source,
  sourceName,
  stamp: stampProp,
  fit = 'regions',
  highlight,
  outlinedOf,
  basemap: basemapProp,
  basemapSrc,
  parcels,
  frame,
  quiet,
  noun: nounProp,
  incomplete: incompleteProp,
  events,
  children,
  className,
  onViewChange,
  linkedKey = null,
  onLinkedKeyChange,
  clusterPins = true,
  clusterCellPx = ATLAS_PIN_CLUSTER_RADIUS_PX,
  clusterStageHint,
  clusterStageHintPhone,
  amenities,
  hidePriceScrubber = false,
  selectedSubdivisionId = null,
  onSubdivisionSelect,
  childDoorNames = null,
  memberKeysBySlug = null,
  taxlotBoundaries,
  onFilterChange,
  keyBasis = false,
}: V3AtlasProps) {
  const uid = useId()
  const router = useRouter()
  const Heading = headingLevel === 1 ? 'h1' : 'h2'

  /* UXLIVE-3: the population and the basemap may arrive after paint. Until
     the dots do, the server's summary stands in for every figure, so the
     server HTML and the hydrated page print the same counts. */
  const [fetchedDots, setFetchedDots] = useState<FetchedDots | null>(null)
  const [dotsFailed, setDotsFailed] = useState(false)
  const [fetchedBasemap, setFetchedBasemap] = useState<Basemap | null>(null)
  /* Matt 2026-09-23, requirement 5: once a district is selected, its own
     tight bbox is small enough that basemapForFrame includes the named
     local-street tier (lib/geo/basemap-streets.ts) alongside the highway
     skeleton — this OUTRANKS the page's own city-wide basemap while a
     selection is live, and clears when it is not (effect further below). */
  const [selectionBasemap, setSelectionBasemap] = useState<Basemap | null>(null)
  /* Matt 2026-09-23, requirement 6: the fetched tax-lot lines for the
     selected district (see the effect further below); null when nothing is
     selected or the page named no boundary for that rail id. */
  const [selectionTaxlots, setSelectionTaxlots] = useState<readonly AtlasParcel[] | null>(null)
  const deferDots = Boolean(dotsSrc) && dotsProp.length === 0
  const dots: readonly AtlasDot[] = fetchedDots?.dots ?? dotsProp
  const waiting = deferDots && fetchedDots == null
  const pre: AtlasDotSummary | null = waiting && dotsSummary ? dotsSummary : null
  const stamp = fetchedDots?.stamp ?? stampProp
  // A deferred map with no summary has no honest count to print until the
  // dots land; a fetched read that came back short says so like any other.
  const incomplete = Boolean(incompleteProp) || fetchedDots?.complete === false || (waiting && !dotsSummary)
  const basemap = selectionBasemap ?? basemapProp ?? fetchedBasemap

  /* Geometry: rings, label anchors, areas, projection — once per data set. */
  const shapes = useMemo<RegionShape[]>(() => {
    return [...regions, ...childRegions].map((r) => {
      const rings = outerRings(r.geometry)
      const anchor = labelAnchor(rings)
      const b = bboxOfRings(rings)
      const area = b ? (b.maxLon - b.minLon) * (b.maxLat - b.minLat) : 0
      // Label under the silhouette's bottom edge so the dense centre stays clean.
      const labelAt = anchor && b ? ([anchor[0], b.minLat] as const) : anchor
      return { ...r, rings, anchor: labelAt, area, bbox: b }
    })
  }, [regions, childRegions])
  const childIdSet = useMemo(() => hierarchyChildIdSet(childRegions), [childRegions])
  const pairedSelection = typeof onSubdivisionSelect === 'function'
  /* The rail-selected child's own shape, independent of hover/pinned. The
     camera fit, the shading, the interior label, the district-scoped
     basemap/lots fetch and the homes-focus filter all key off this ONE
     lookup so none of them can ever disagree about which polygon is
     selected (Matt 2026-09-23). Computed early (before `isOn`, which reads
     it) rather than beside the camera-fit effect that also uses it. */
  const selectedChildShape = useMemo(() => {
    if (!pairedSelection || !selectedSubdivisionId) return null
    return (
      shapes.find((item) => childIdSet.has(item.id) && childSelectionMatches(item.id, selectedSubdivisionId)) ?? null
    )
  }, [pairedSelection, selectedSubdivisionId, shapes, childIdSet])
  /* Matt 2026-09-23, requirement 4: the SAME membership the rail's own
     counts use (childListingKeys in lib/place/place-child-stock.ts), so the
     map can never show a different population than the rail line beside it
     implies. An id with no recorded keys is an empty Set, not "show
     everything" — matching PlaceSubdivisionHomes's own filter exactly. */
  const selectionKeys = useMemo(() => {
    if (!selectedChildShape || !memberKeysBySlug) return null
    return new Set(memberKeysBySlug[childSelectionId(selectedChildShape.id)] ?? [])
  }, [selectedChildShape, memberKeysBySlug])

  const proj = useMemo(() => {
    // UXLIVE-3: a deferred population is framed by the server, from the full
    // population, with the same function below; the frame holds when the
    // dots land, so nothing jumps.
    if (deferDots && dotsSummary) return atlasProjection(dotsSummary.box)
    // An explicit frame wins; a record map (fit: dots) frames the dots and
    // the towns holding them; otherwise the base silhouettes plus the dots'
    // 1st to 99th percentile. See atlasFrameBox (lib/atlas/atlas-derive.ts)
    // and lib/geo/record-frame.ts.
    return atlasProjection(atlasFrameBox({ frame, subjectGrain, fit, dots, shapes }))
  }, [deferDots, dotsSummary, shapes, dots, fit, frame, subjectGrain])


  const paths = useMemo<PlacedShape[]>(
    () => shapes.map((s) => ({ ...s, d: ringsToPath(s.rings, proj) })),
    [shapes, proj],
  )

  /* Lot lines, projected the same way as every other geometry on this map, so
     a parcel edge meets the plat outline it actually sits inside. */
  /* The caller's own lot lines (a listing or plat page's `parcels` prop) win
     when present; the lazily-fetched district lots (requirement 6) are the
     ONLY lot source a paired-selection city page ever has, and the two
     never coexist — no existing caller passes `parcels` alongside
     `onSubdivisionSelect`. */
  const effectiveParcels: readonly AtlasParcel[] = useMemo(
    () => (selectionTaxlots && selectionTaxlots.length > 0 ? selectionTaxlots : parcels ?? []),
    [parcels, selectionTaxlots],
  )
  const parcelPaths = useMemo(() => {
    if (effectiveParcels.length === 0) return []
    return effectiveParcels
      .map((p) => ({ id: p.id, subject: p.subject, name: p.name, d: ringsToPath(outerRings(p.geometry), proj) }))
      .filter((p) => p.d.length > 0)
  }, [effectiveParcels, proj])

  /* The basemap, decoded once and projected with the same functions the
     recorded boundaries use — one projection, so a road meets the city limit
     it actually runs through. */
  const basemapPaths = useMemo(() => {
    if (!basemap) return null
    const place = (features: readonly BasemapFeature[], close: boolean) =>
      features
        .flatMap((f) =>
          decodeBasemapFeature(f, basemap.q).map((points) => ({
            cls: f.c,
            name: f.n,
            d: close ? ringsToPath([points], proj) : pointsToPath(points, proj),
          })),
        )
        .filter((p) => p.d.length > 0)
    const built = {
      bodies: place(basemap.bodies, true),
      waterways: place(basemap.waterways, false),
      roads: place(basemap.roads, false),
    }
    return built.bodies.length + built.waterways.length + built.roads.length > 0 ? built : null
  }, [basemap, proj])

  const amenityParks = amenities?.parks ?? []
  const amenityTrails = amenities?.trails ?? []
  const [showParks, setShowParks] = useState(true)
  const [showTrails, setShowTrails] = useState(true)

  const amenityParkPaths = useMemo(() => {
    return amenityParks
      .map((park) => ({ id: park.id, name: park.name, d: ringsToPath(outerRings(park.geometry), proj) }))
      .filter((park) => park.d.length > 0)
  }, [amenityParks, proj])

  const amenityTrailPaths = useMemo(() => {
    const out: { id: string; name: string; d: string }[] = []
    amenityTrails.forEach((trail, index) => {
      lineStringParts(trail.geometry).forEach((points, part) => {
        const d = pointsToPath(points, proj)
        if (d) out.push({ id: `${trail.id}:${index}:${part}`, name: trail.name, d })
      })
    })
    return out
  }, [amenityTrails, proj])

  const towns = useMemo(() => paths.filter((s) => s.kind === 'town'), [paths])
  /* Largest first, so a community inside a neighborhood is painted on top
     and takes the pointer. */
  const places = useMemo(() => paths.filter((s) => s.kind !== 'town').sort((a, b) => b.area - a.area), [paths])
  /* A town whose silhouette spans most of the frame IS the frame (a scoped
     page's own place): the headline names it, so its label would only clip
     at the edge (pass five). */
  const isFrame = useCallback(
    (s: RegionShape) => {
      // A record map's frame is computed, not named by the headline: the town
      // filling it is the answer to "where", so it keeps its label.
      if (fit === 'dots') return false
      if (!s.bbox) return false
      const [x0, y0] = proj.toXY(s.bbox.minLon, s.bbox.maxLat)
      const [x1, y1] = proj.toXY(s.bbox.maxLon, s.bbox.minLat)
      return Math.abs(x1 - x0) / proj.width > 0.8 || Math.abs(y1 - y0) / proj.height > 0.8
    },
    [proj, fit],
  )
  const wide = proj.width / proj.height > 1.35
  /* A frame at least as wide as it is tall does not need the phone's
     minimum height: the aspect already gives it one, and the floor was
     leaving 16% of the stage empty at 375 (evaluator round five,
     LISTING-NOBOUNDARY-9). */
  const fitsPhone = proj.width / proj.height >= 1

  /* The page's own place: the one town whose silhouette IS the frame (a
     scoped place page). Its name opens most of its children's plat names and
     the door labels drop it, because a neighborhood's plats read as its own
     children: on Awbrey Butte's page "Awbrey Butte Homesites Phase Twenty-two"
     is the door "Homesites Phase Twenty-two". The homepage frames several
     towns and none fills it, so nothing is stripped there (SITE-07 quality
     pass, 2026-09-09). */
  const ownPlaceName = useMemo(() => {
    const framed = towns.filter(isFrame)
    return framed.length === 1 ? framed[0]!.name : null
  }, [towns, isFrame])
  /* One label per place, computed over the WHOLE set so a chip, its map label,
     its card and its polygon's accessible name all agree (the tap-target gate
     pairs polygon to chip by that name), and so siblings that differ only by
     phase keep the phase instead of collapsing to one identical string
     (lib/place/short-place-label, placeDoorLabels). */
  const doorLabels = useMemo(() => {
    const visitable = places.filter((s) => publishPlatDisplayName(s.name))
    const labels = placeDoorLabels(
      visitable.map((s) => publishPlatDisplayName(s.name) ?? s.name),
      ownPlaceName,
    )
    return new Map(visitable.map((s, i) => [s.id, labels[i] ?? publishPlatDisplayName(s.name) ?? s.name]))
  }, [places, ownPlaceName])
  const doorLabel = useCallback(
    (s: RegionShape) => {
      const published = publishPlatDisplayName(s.name)
      if (!published) return ''
      return doorLabels.get(s.id) ?? shortPlaceLabel(published)
    },
    [doorLabels],
  )

  const xy = useMemo(() => dots.map((d) => proj.toXY(d.lng, d.lat)), [dots, proj])

  const membership = useMemo(() => atlasMembership(dots, shapes), [dots, shapes])

  /* The map is one tab stop: the roving index says which place holds it. */
  const placesRef = useRef<SVGGElement>(null)
  const [roving, setRoving] = useState(0)

  /* The measured view: where the viewBox lands inside the stage (meet fit).
     City fold seeds this from the desktop hint so SSR already has bubbles.
     Membership does NOT follow this box — pinMarks locks to the fold stage.
     Layout effect + offset size so a collapsed GBR cannot paint 1 × 759. */
  const stageRef = useRef<HTMLDivElement>(null)
  const [view, setView] = useState<View | null>(() =>
    clusterStageHint
      ? atlasViewFromStage(clusterStageHint.w, clusterStageHint.h, proj.width, proj.height)
      : null,
  )
  useLayoutEffect(() => {
    const el = stageRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => {
      const w = Math.max(el.offsetWidth, el.getBoundingClientRect().width)
      const h = Math.max(el.offsetHeight, el.getBoundingClientRect().height)
      if (w <= 0 || h <= 0) return
      const measured = atlasViewFromStage(w, h, proj.width, proj.height)
      if (clusterStageHint) {
        const fold = pickCityFoldClusterStage(w, clusterStageHint, clusterStageHintPhone ?? clusterStageHint)
        setView(floorCityFoldPaintView(measured, fold, proj.width, proj.height))
        return
      }
      setView(measured)
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    measure()
    return () => ro.disconnect()
  }, [proj.width, proj.height, clusterStageHint, clusterStageHintPhone])

  const toPx = useCallback(
    (x: number, y: number): readonly [number, number] => (view ? [view.ox + x * view.scale, view.oy + y * view.scale] : [0, 0]),
    [view],
  )

  const [cam, setCam] = useState<AtlasCam>(ATLAS_CAM_HOME)
  const camRef = useRef(cam)
  camRef.current = cam
  const dragRef = useRef<{ x: number; y: number; moved: boolean } | null>(null)
  const ptsRef = useRef(new Map<number, { x: number; y: number }>())
  const pinchRef = useRef<{ d: number } | null>(null)
  const stageSize = view ? { w: view.w, h: view.h } : { w: 1, h: 1 }

  useEffect(() => {
    const el = stageRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const r = el.getBoundingClientRect()
      const dy = e.deltaY
      if (dy === 0) return
      const factor = e.ctrlKey ? Math.exp(-dy * 0.01) : dy < 0 ? 1.18 : 1 / 1.18
      setCam((c) => zoomAt(c, e.clientX - r.left, e.clientY - r.top, factor, r.width, r.height))
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [view?.w, view?.h])

  useEffect(() => {
    const t = window.setTimeout(() => {
      const next =
        !view || view.scale <= 0 || cam.k === 1 ? null : visibleLonLat(cam, view, proj.toXY)
      onViewChange?.(next)
      publishAtlasView(next)
    }, 100)
    return () => window.clearTimeout(t)
  }, [cam, view, proj, onViewChange])

  /* In view? The pulses only breathe while the reader can see them. */
  const sectionRef = useRef<HTMLElement>(null)
  const [inView, setInView] = useState(true)
  useEffect(() => {
    const el = sectionRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([entry]) => setInView(Boolean(entry?.isIntersecting)), { threshold: 0.05 })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  /* UXLIVE-3: fetch the deferred dots and basemap once the map is within a
     screen of the viewport and the page has gone idle. One retry each; a
     failed dots read keeps the server's figures and says so in the source. */
  const wantBasemap = !basemapProp && Boolean(basemapSrc)
  useEffect(() => {
    if (!deferDots && !wantBasemap) return
    const controller = new AbortController()
    const cancel = whenNearAndIdle(sectionRef.current, () => {
      if (deferDots && dotsSrc) {
        void fetchJsonOnce(dotsSrc, asFetchedDots, controller.signal).then((got) => {
          if (controller.signal.aborted) return
          if (got) setFetchedDots(got)
          else setDotsFailed(true)
        })
      }
      if (wantBasemap && basemapSrc) {
        void fetchJsonOnce(basemapSrc, asFetchedBasemap, controller.signal).then((got) => {
          if (got && !controller.signal.aborted) setFetchedBasemap(got)
        })
      }
    })
    return () => {
      cancel()
      controller.abort()
    }
  }, [deferDots, dotsSrc, wantBasemap, basemapSrc])

  /* Price scale: the scrubber runs from the 5th to the 95th percentile of the
     listed dots' own prices, rounded outward to a clean step. */
  const priceScale = useMemo(() => (pre ? pre.priceScale : atlasPriceScale(dots)), [pre, dots])

  /* The scrubber's own distribution (2026-09-25: a bare track said nothing
     about where the homes sit): the active dots' asks in equal steps across
     the scale, the ones past either end counted in the end step, so the
     slider is drawn over how many homes it will keep or drop. Counts of the
     dots on this map, nothing else; a withheld ask is not binned. */
  const priceBins = useMemo((): { kind: 'bins'; bins: number[] } | { kind: 'ticks'; xs: number[] } | null => {
    const span = priceScale.max - priceScale.min
    if (!(span > 0)) return null
    const asks: number[] = []
    for (const d of dots) {
      if (d.s !== 'active' || d.p == null || !Number.isFinite(d.p)) continue
      asks.push(d.p)
    }
    if (asks.length < 3) return null
    // A few homes are a tick each (2026-09-29: thirteen Tetherow asks in
    // twenty-four steps drew stray blocks that read as noise); a set large
    // enough to have a shape is drawn in steps.
    if (asks.length < PRICE_TICKS_UNDER) {
      return { kind: 'ticks', xs: asks.map((p) => Math.min(1, Math.max(0, (p - priceScale.min) / span))) }
    }
    const bins = new Array<number>(PRICE_BINS).fill(0)
    for (const p of asks) {
      const at = Math.floor(((p - priceScale.min) / span) * PRICE_BINS)
      bins[Math.min(PRICE_BINS - 1, Math.max(0, at))] += 1
    }
    return { kind: 'bins', bins }
  }, [dots, priceScale])

  /* Visitor state. */
  const [maxPriceRaw, setMaxPriceRaw] = useState<number | null>(null)
  const maxPrice = maxPriceRaw ?? priceScale.max
  const atCeiling = maxPrice >= priceScale.max
  const setMaxPrice = (v: number) => setMaxPriceRaw(v >= priceScale.max ? null : v)
  const [offTypes, setOffTypes] = useState<ReadonlySet<string>>(() => new Set())
  const [hover, setHover] = useState<string | null>(null)
  /* The phone fold on the chips: closed on arrival, so a page with eighty
     plats opens on its first eight and one honest count of the rest. */
  const [chipsOpen, setChipsOpen] = useState(false)
  const [pinned, setPinned] = useState<{ id: string; at: readonly [number, number] } | null>(null)
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [cardH, setCardH] = useState(240)

  /* ONE filter for every layer, sold included. Folding the selected
     district's membership in here (rather than a second filter downstream)
     means every count, key, pulse and heat cell that already reads isOn
     focuses on the district for free — the same rule the price and type
     filters already get applied through. */
  const isOn = useCallback(
    (d: AtlasDot) =>
      !offTypes.has(d.t) &&
      (atCeiling || d.p == null || d.p <= maxPrice) &&
      (!selectionKeys || selectionKeys.has(d.k)),
    [offTypes, atCeiling, maxPrice, selectionKeys],
  )

  /* The counts the marks are drawn from (atlasCounts). Before deferred dots
     land, the server's counts for the same population at rest. */
  const counts = useMemo(
    () => (pre ? { ...pre.counts, listed: [] as number[] } : atlasCounts(dots, isOn)),
    [pre, dots, isOn],
  )
  /* Dots the frame does not hold: counted in every figure, named in the
     source line, never silently missing (atlasBeyond). */
  const beyond = useMemo(() => (pre ? pre.beyond : atlasBeyond(dots, proj, isOn)), [pre, dots, proj, isOn])

  /* A record map: every dot is a closing, none is for sale. Read from the
     whole population, never the filtered counts: an empty price filter must
     not turn a broker's record into a for-sale map (pass three, D1). */
  const closingsMap = useMemo(() => (pre ? pre.closingsMap : atlasIsClosingsMap(dots)), [pre, dots])

  const typesOn = useMemo(() => types.filter((t) => !offTypes.has(t.key)), [types, offTypes])
  const allTypesOn = typesOn.length === types.length

  /* W1: the basis the key names for the reader's filter (mapListingCount),
     and the filter itself reported to a homes block beside the map
     (onFilterChange) so its dials and their headers follow the key. The
     callback rides a ref so a parent re-render never re-reports it. */
  const filterBasis = mapListingCount(
    { forSale: 0 },
    { typesOn, types, maxPriceLabel: atCeiling ? null : fmtShort(maxPrice) },
  ).basis
  // Strings, so a fresh `types` array from a parent re-render is not a change.
  const filterBasisKey = filterBasis.key
  const filterBasisLabel = filterBasis.label
  const onFilterChangeRef = useRef(onFilterChange)
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange
  }, [onFilterChange])
  const filterReported = useRef(false)
  useEffect(() => {
    const report = onFilterChangeRef.current
    if (!report) return
    // Nothing to tell the block on arrival: it starts unfiltered too.
    if (!filterReported.current && offTypes.size === 0 && maxPriceRaw == null) return
    filterReported.current = true
    report({
      offTypes: [...offTypes],
      maxPrice: maxPriceRaw,
      basis: { key: filterBasisKey, label: filterBasisLabel },
    })
  }, [offTypes, maxPriceRaw, filterBasisKey, filterBasisLabel])

  const noun = useCallback(
    (n: number) => (nounProp ? (n === 1 ? nounProp.one : nounProp.many) : nounFor(n, typesOn, types)),
    [nounProp, typesOn, types],
  )
  /* The median is of HOMES unless the reader chose lots or commercial alone:
     a lot's price beside a house's is not one median. */
  const medianScope = useMemo(
    () =>
      atlasMedianScope(
        typesOn.map((t) => t.key),
        closingsMap,
      ),
    [typesOn, closingsMap],
  )

  /* Areas by id: on a record map a closing counts in ONE place, the smallest
     that holds it (pass three, D4; evaluator round five, TEAM-REBECCA-3). */
  const areaById = useMemo(() => new Map(shapes.map((s) => [s.id, s.area])), [shapes])

  /* Per-place figures over the listed dots on screen — the card's numbers
     (atlasRegionStats). Before deferred dots land, the server's per-place
     figures for the same population at rest. */
  const regionStats = useMemo(() => {
    if (pre) {
      const out = new Map<string, AtlasPlaceStat>()
      for (const [id, [n, med]] of Object.entries(pre.places)) out.set(id, { n, median: med })
      return out
    }
    return atlasRegionStats({
      dots,
      listed: counts.listed,
      membership,
      medianKeys: medianScope.keys,
      closingsMap,
      areaById,
    })
  }, [pre, counts.listed, membership, dots, medianScope, closingsMap, areaById])



  /* Every place as a door a thumb can hit: on a phone most silhouettes are
     under 20px, so the chips carry the reach the map cannot (pass five, R2).
     Places with listings first, by count; empty ones after, still doors. */
  /* A record map draws only the places the record touches; an outline with
     nothing in it would open a card reading 0 closings (pass three, D5). */
  const drawnPlaces = useMemo(
    () => (closingsMap ? places.filter((s) => (regionStats.get(s.id)?.n ?? 0) > 0) : places),
    [places, closingsMap, regionStats],
  )
  /* A filter can shrink the drawn set under the tab stop: keep it in range, or
     the map loses its one keyboard entry. */
  useEffect(() => {
    setRoving((r) => (r < drawnPlaces.length ? r : 0))
  }, [drawnPlaces.length])

  /* What the source line may claim: the outlines actually on screen. A record
     map draws a subset, and a frame clips what falls outside it, so counting
     the regions the page handed over overstated it by two to one (evaluator
     round five, TEAM-MATT-2). */
  const drawnCount = towns.length + drawnPlaces.length


  /* What the numbers are filtered to, in the reader's words. A claim that says
     only "under $700K" while three of four types are switched off is counting
     one thing and describing another (evaluator round five, TEAM-MATT-3). */
  const filterPhrase = useMemo(() => {
    if (allTypesOn) return ''
    const on = typesOn.map((t) => t.label.toLowerCase())
    // One type on: the noun already IS that type ("houses"), so naming it here
    // too printed "274 house houses for sale" (round six).
    if (on.length <= 1) return ''
    return `${on.slice(0, -1).join(', ')} and ${on[on.length - 1]!} `
  }, [allTypesOn, typesOn])

  const claim = useMemo(() => {
    if (claimText) return claimText
    if (incomplete) return 'Live counts are unavailable right now. The map shows what could be read.'
    // SITE_PAGES / PLACE_PAGES kill list: no old-style triple-count line (every
    // type for sale, pending, sold in the last 30 days) with its trailing
    // how-to-zoom instruction under the Atlas headline. Both stay dead. A caller that
    // asks for `claimTone="inventory"` gets ONE clause off the same filtered
    // count the marks use, with the feed named — the first read the taste table
    // of 2026-09-08 said this section did not have.
    if (!closingsMap) {
      if (claimTone !== 'inventory') return ''
      const ceiling = atCeiling ? '' : ` under ${fmtShort(maxPrice)}`
      // "ON THIS MAP" IS NOT FILLER, it is §0 rule 5. The Atlas draws every
      // property type inside the boundary; the page's own opening counts the
      // single-family set the browse door opens. On /subdivisions/park-addition
      // those are 4 and 3, a screen apart, and without a phrase scoping this
      // one to the map a reader meets two counts of what looks like one thing.
      const sentence = `${counts.forSale.toLocaleString('en-US')} ${filterPhrase}${noun(counts.forSale)} for sale on this map right now${ceiling}`
      return sourceName ? `${sentence}, ${sourceName}.` : `${sentence}.`
    }
    const ceiling = atCeiling ? '' : ` under ${fmtShort(maxPrice)}`
    return `${counts.closed.toLocaleString('en-US')} ${filterPhrase}${noun(counts.closed)}${ceiling}.`
  }, [claimText, counts, atCeiling, maxPrice, noun, incomplete, filterPhrase, closingsMap, claimTone, sourceName])

  /* The pulses: the newest real events, with slots per kind so closes always
     show; capped so the map breathes and the main thread never notices. */
  const pulses = useMemo(() => {
    const byKind: Record<'new' | 'pending' | 'sold', { i: number; recency: number }[]> = { new: [], pending: [], sold: [] }
    dots.forEach((d, i) => {
      if (!isOn(d)) return
      if (isAtlasPulseSold(d) && d.soldAgo != null) byKind.sold.push({ i, recency: d.soldAgo })
      else if (d.s === 'active' && d.age != null && d.age <= 7) byKind.new.push({ i, recency: d.age })
      else if (d.s === 'pending' && d.age != null && d.age <= 14) byKind.pending.push({ i, recency: d.age })
    })
    const out: { i: number; kind: 'new' | 'pending' | 'sold' }[] = []
    for (const kind of ['new', 'pending', 'sold'] as const) {
      byKind[kind].sort((a, b) => a.recency - b.recency)
      for (const { i } of byKind[kind].slice(0, PULSE_SLOTS[kind])) out.push({ i, kind })
    }
    return out
  }, [dots, isOn])

  /* Sales heat: kernel density of closings in this map's projection. Quiet
     maps, incomplete reads, and a broker's record of closings (the dots ARE
     the subject) draw none. */
  const heat = useMemo(() => {
    if (quiet || incomplete || closingsMap) return { cells: [] as const, n: 0, max: 0 }
    const points: { x: number; y: number }[] = []
    dots.forEach((d, i) => {
      if (!isAtlasHeatClosing(d.s) || !isOn(d)) return
      const p = xy[i]
      if (!p) return
      points.push({ x: p[0], y: p[1] })
    })
    return salesHeatField(points, { width: proj.width, height: proj.height })
  }, [quiet, incomplete, closingsMap, dots, isOn, xy, proj.width, proj.height])

  /* ROW ↔ MARK, the list's direction. One index, so a hover over a sibling
     list re-renders one mark instead of every dot on the map. */
  const linkedIndex = useMemo(() => {
    if (!linkedKey) return null
    const i = dots.findIndex((d) => d.k === linkedKey)
    return i >= 0 ? i : null
  }, [dots, linkedKey])

  const active = pinned?.id ?? hover
  const activeShape = active ? paths.find((s) => s.id === active) ?? null : null
  const activeStats = active ? regionStats.get(active) ?? { n: 0, median: null } : null
  const inActivePlace = useCallback(
    (i: number) => Boolean(active && (membership[i] ?? []).includes(active)),
    [active, membership],
  )

  /* Every dot in stage pixels, so the nearest one to a pointer is a scan and
     not a projection per event. */
  const dotPx = useMemo(() => (view ? xy.map(([x, y]) => toPx(x, y)) : []), [xy, toPx, view])

  /* The mark under the pointer. Until now the dots — the data the map is made
     of — answered nothing: they are pointer-transparent so 4,676 of them cost
     no hit testing, and on a frame with no places the whole map was inert
     (evaluator round five, LISTING-NOBOUNDARY-2, LISTING-BEND-7). */
  const [dotHit, setDotHit] = useState<number | null>(null)
  const [clusterHit, setClusterHit] = useState<string | null>(null)
  const pinMarksRef = useRef<PaintedPin[]>([])
  /* Price pills are ~52×22 above the coordinate; 14px only caught the caret. */
  const REACH = 28

  /* ROW ↔ MARK, the map's direction: the key under the pointer, reported up
     so a sibling list can raise its own row. */
  const hitKey = dotHit != null ? (dots[dotHit]?.k ?? null) : null
  useEffect(() => {
    onLinkedKeyChange?.(hitKey)
  }, [hitKey, onLinkedKeyChange])

  const nearestDot = useCallback(
    (wx: number, wy: number, reach: number): number | null => {
      if (dotPx.length === 0) return null
      let best: number | null = null
      let bestD = reach * reach
      for (let i = 0; i < dotPx.length; i += 1) {
        const d = dots[i]
        if (!d || !isOn(d)) continue
        // Sold closes are the wash, not marks, except on a record map
        // or the home this page is about.
        if (d.s === 'sold' && !closingsMap && d.k !== highlight?.key) continue
        const p = dotPx[i]
        if (!p) continue
        const dx = p[0] - wx
        const dy = p[1] - wy
        const dd = dx * dx + dy * dy
        if (dd < bestD) {
          bestD = dd
          best = i
        }
      }
      return best
    },
    [dotPx, dots, isOn, closingsMap, highlight],
  )

  const layerMarksOf = (marks: readonly PaintedPin[]) =>
    marks.map((m) =>
      m.kind === 'cluster'
        ? { kind: 'cluster' as const, id: m.id, x: m.x, y: m.y }
        : { kind: 'pin' as const, id: m.d.k, i: m.i, x: m.x, y: m.y },
    )

  const applyLayerHit = useCallback((px: number, py: number, wx: number, wy: number) => {
    const layer = hitAtlasPinLayer(px, py, layerMarksOf(pinMarksRef.current), REACH)
    if (layer?.kind === 'cluster') {
      setClusterHit(layer.id)
      setDotHit(null)
      return layer
    }
    if (layer?.kind === 'pin') {
      setClusterHit(null)
      setDotHit(layer.i)
      return layer
    }
    setClusterHit(null)
    const fallback = nearestDot(wx, wy, REACH / camRef.current.k)
    setDotHit(fallback)
    return fallback != null ? ({ kind: 'pin' as const, i: fallback }) : null
  }, [nearestDot])

  const pointerOnStage = (e: { clientX: number; clientY: number }) => {
    const el = stageRef.current
    if (!el) return null
    const r = el.getBoundingClientRect()
    return { px: e.clientX - r.left, py: e.clientY - r.top }
  }

  const zoomBy = useCallback((factor: number) => {
    const el = stageRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setCam((c) => zoomAt(c, r.width / 2, r.height / 2, factor, r.width, r.height))
  }, [])

  const zoomIntoCluster = useCallback(
    (indices: readonly number[]) => {
      if (!view) return
      const pts: { x: number; y: number }[] = []
      for (const i of indices) {
        const p = dotPx[i]
        if (p) pts.push({ x: p[0], y: p[1] })
      }
      if (atlasClusterCanExpand(pts, ATLAS_K_MAX, ATLAS_PIN_CLUSTER_RADIUS_PX)) {
        const box = atlasClusterWorldBounds(pts)
        if (box) setCam(fitRect(box, view.w, view.h, 0.22))
        return
      }
      const first = indices.map((i) => dots[i]).find((d) => d?.href)
      if (first?.href) router.push(first.href)
    },
    [view, dotPx, dots, router],
  )

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      const at = pointerOnStage(e)
      if (!at) return
      ptsRef.current.set(e.pointerId, { x: at.px, y: at.py })
      if (ptsRef.current.size >= 2 && pinchRef.current) {
        const pts = [...ptsRef.current.values()]
        const a = pts[0]
        const b = pts[1]
        if (!a || !b) return
        const d = Math.hypot(a.x - b.x, a.y - b.y)
        if (!(d > 0) || !(pinchRef.current.d > 0)) return
        const factor = d / pinchRef.current.d
        const midX = (a.x + b.x) / 2
        const midY = (a.y + b.y) / 2
        setCam((c) => zoomAt(c, midX, midY, factor, stageSize.w, stageSize.h))
        pinchRef.current.d = d
        if (dragRef.current) dragRef.current.moved = true
        return
      }
      const drag = dragRef.current
      if (drag && e.buttons) {
        const dx = at.px - drag.x
        const dy = at.py - drag.y
        if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true
        if (drag.moved) {
          setCam((c) => panBy(c, dx, dy, stageSize.w, stageSize.h))
          drag.x = at.px
          drag.y = at.py
          return
        }
      }
      setPointer({ x: at.px, y: at.py })
      const [wx, wy] = screenToWorld(camRef.current, at.px, at.py)
      applyLayerHit(at.px, at.py, wx, wy)
    },
    [nearestDot, applyLayerHit, stageSize.w, stageSize.h],
  )

  /* A camera move that should be SEEN moving (a fit, a return home) plays a
     CSS transition on .v3-atlas__world for one beat; a drag or a wheel zoom
     never does, or panning would feel like it is fighting a spring. The
     transition itself reads the shared duration tokens, so
     prefers-reduced-motion collapses it to instant through the existing
     blanket rule (tokens.css) with no separate check needed here (Matt
     2026-09-23: "animated unless prefers-reduced-motion, then instant"). */
  const [fitting, setFitting] = useState(false)
  const fitTimerRef = useRef<number | null>(null)
  const animateCamTo = useCallback((next: AtlasCam) => {
    setFitting(true)
    setCam(next)
    if (fitTimerRef.current != null) window.clearTimeout(fitTimerRef.current)
    fitTimerRef.current = window.setTimeout(() => setFitting(false), 450)
  }, [])
  useEffect(
    () => () => {
      if (fitTimerRef.current != null) window.clearTimeout(fitTimerRef.current)
    },
    [],
  )

  const fitCamToShape = useCallback(
    (shape: RegionShape, pad = 0.08) => {
      if (!shape.bbox || !view || view.w <= 0 || view.h <= 0) return
      const [x0, y0] = toPx(...proj.toXY(shape.bbox.minLon, shape.bbox.maxLat))
      const [x1, y1] = toPx(...proj.toXY(shape.bbox.maxLon, shape.bbox.minLat))
      animateCamTo(
        fitRect(
          {
            x0: Math.min(x0, x1),
            y0: Math.min(y0, y1),
            x1: Math.max(x0, x1),
            y1: Math.max(y0, y1),
          },
          view.w,
          view.h,
          pad,
        ),
      )
    },
    [proj, toPx, view, animateCamTo],
  )

  const openPlace = useCallback(
    (shape: PlacedShape) => {
      const at = shape.anchor
        ? toPx(...proj.toXY(shape.anchor[0], shape.anchor[1]))
        : pointer
          ? screenToWorld(camRef.current, pointer.x, pointer.y)
          : ([0, 0] as const)
      setPinned((prev) => (prev?.id === shape.id ? null : { id: shape.id, at: [at[0], at[1]] }))
      setHover(shape.id)
      // Child plat: zoom so THAT recorded boundary fills the frame.
      // Subject town: return home. Homepage / city places stay pin-only.
      // Listing pins keep their own click path.
      if (shape.kind === 'town') {
        animateCamTo(ATLAS_CAM_HOME)
        onSubdivisionSelect?.(null)
      } else if (childIdSet.has(shape.id)) {
        fitCamToShape(shape)
        onSubdivisionSelect?.(childSelectionId(shape.id))
      }
    },
    [childIdSet, fitCamToShape, onSubdivisionSelect, pointer, proj, toPx, animateCamTo],
  )

  const zoomedSelection = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    if (!pairedSelection || !view) return
    const next = selectedSubdivisionId ?? null
    if (zoomedSelection.current === next) return
    zoomedSelection.current = next
    if (!next) {
      animateCamTo(ATLAS_CAM_HOME)
      return
    }
    // Extra padding over the plain map-click fit (0.08): a rail selection is
    // the map's own focus gesture, not a tap on an already-visible shape, so
    // the district label and any pill hugging the polygon's edge need the
    // room (Matt 2026-09-23, requirement 4).
    if (selectedChildShape) fitCamToShape(selectedChildShape, 0.16)
  }, [pairedSelection, selectedSubdivisionId, view, selectedChildShape, fitCamToShape, animateCamTo])

  /* Every place as a door a thumb can hit: on a phone most silhouettes are
     under 20px, so the chips carry the reach the map cannot (pass five, R2).
     Places with listings first, by count; empty ones after, still doors. */
  const chipPlaces = useMemo(
    () =>
      incomplete
        ? []
        : places
            .map((s) => ({ shape: s, n: regionStats.get(s.id)?.n ?? 0 }))
            // A record map lists only the places the record touches; a rail
            // of zeros is brokerage chrome on a personal page (C5).
            .filter((r) => !closingsMap || r.n > 0)
            .filter((r) => Boolean(publishPlatDisplayName(r.shape.name)))
            .sort((a, b) => b.n - a.n || a.shape.name.localeCompare(b.shape.name))
            /* A rail, not a list: the fullest places (E5) — but never fewer
               than the map DRAWS.
               The cap was a flat 24 while the homepage draws 27, so the three
               with the fewest listings — Crosswater, Vandevert Ranch, Awbrey
               Glen — were rendered as buttons with no reachable target
               anywhere. They are also three of the four smallest polygons on
               the map (5x11, 5x9, 5x8 at 375), so the chip was the only thumb
               could ever reach them by. Every chip measures 100x44; a drawn
               place without one is a promise the page cannot keep. */
            .slice(0, Math.max(24, drawnPlaces.length)),
    [places, regionStats, incomplete, closingsMap, drawnPlaces.length],
  )
  /* THE DOORS: a drawn polygon is a control only when its full-size partner
     is on the page, and it carries the partner's name (lib/atlas/atlas-doors).
     Paired, the partner is the rail row beside the map; otherwise it is the
     chip above. CI 2026-10-07: on a short listing read /cities/bend framed
     the region and drew Summit West, Southern Crossing and Southwest Bend
     under 44px (the smallest 8.8x9.3) as role="button" while their rail rows
     sat folded with nothing tying them to "Show all"; and a short read prints
     no chips at all while every polygon here stayed a button. */
  const doors = useMemo(
    () =>
      atlasPlaceDoors({
        drawn: drawnPlaces,
        paired: pairedSelection ? { childIds: childIdSet, railNames: childDoorNames ?? {} } : null,
        chips: chipPlaces.map((r) => r.shape),
        chipLabel: doorLabel,
      }),
    [drawnPlaces, pairedSelection, childIdSet, childDoorNames, chipPlaces, doorLabel],
  )
  const doorIndices = useMemo(() => atlasDoorIndices(drawnPlaces, doors), [drawnPlaces, doors])
  /* The one tab stop always sits on a door, never on a drawn non-control. */
  const rovingDoor = doorIndices.includes(roving) ? roving : (doorIndices[0] ?? -1)
  /* One chip. Rendered twice over (the first eight, then the folded rest), so
     the two halves cannot drift apart. */
  const chipButton = (r: (typeof chipPlaces)[number]) => (
    <button
      key={r.shape.id}
      type="button"
      className={cn('v3-atlas__chip', active === r.shape.id && 'is-active')}
      aria-pressed={active === r.shape.id}
      onPointerEnter={() => {
        setDotHit(null)
        setHover(r.shape.id)
      }}
      onPointerLeave={() => {
        if (!pinned) setHover(null)
      }}
      onClick={() => openPlace(r.shape)}
    >
      <span className="v3-atlas__chip-name">{doorLabel(r.shape)}</span>
      {r.n > 0 ? <span className="v3-atlas__chip-n">{r.n.toLocaleString('en-US')}</span> : null}
    </button>
  )

  /* Escape, a click on empty map, or a click outside the stage release a
     pinned card. */
  const dismiss = useCallback(() => {
    setPinned(null)
    setHover(null)
  }, [])
  useEffect(() => {
    if (!pinned) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    const onDown = (e: PointerEvent) => {
      const stage = stageRef.current
      const target = e.target as Element | null
      if (!stage || !target) return
      if (!stage.contains(target)) {
        dismiss()
        return
      }
      const onPlace = target.closest('.v3-atlas__place, .v3-atlas__town, .v3-atlas__card')
      if (!onPlace) dismiss()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onDown)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onDown)
    }
  }, [pinned, dismiss])

  useLayoutEffect(() => {
    const el = cardRef.current
    if (el) setCardH(el.offsetHeight)
  }, [active, pinned, view])

  const toggleType = (key: string) =>
    setOffTypes((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else if (next.size < types.length - 1) next.add(key)
      return next
    })

  /* Choropleth fill step, 0..4, by share of the strongest place. */

  const screenOf = useCallback(
    (lon: number, lat: number): readonly [number, number] => {
      const [x, y] = toPx(...proj.toXY(lon, lat))
      return [x * cam.k + cam.x, y * cam.k + cam.y]
    },
    [toPx, proj, cam.k, cam.x, cam.y],
  )

  /* Matt 2026-09-23, requirement 5: once a district is selected, fetch a
     basemap scoped to its own (padded) bbox — small enough that
     basemapForFrame folds in the named local-street tier alongside the
     highway skeleton (lib/geo/basemap-source.ts, StreetLayer). Clears the
     moment the selection changes or clears, so a deselected map is never
     left showing a stale district's streets. */
  useEffect(() => {
    const bbox = selectedChildShape?.bbox
    if (!bbox) {
      setSelectionBasemap(null)
      return
    }
    const href = atlasBasemapHref({ bbox, pad: 0.35 })
    if (!href) {
      setSelectionBasemap(null)
      return
    }
    const controller = new AbortController()
    void fetchJsonOnce(href, asFetchedBasemap, controller.signal).then((got) => {
      if (!controller.signal.aborted && got) setSelectionBasemap(got)
    })
    return () => controller.abort()
  }, [selectedChildShape])

  /* Matt 2026-09-23, requirement 6: the recorded tax-lot lines inside the
     selected district — fetched only once selected, and only when the page
     named a boundary row for that rail id (taxlotBoundaries). No lot layer
     is ever drawn from the rail id alone. */
  useEffect(() => {
    const scope = selectedChildShape ? taxlotBoundaries?.[childSelectionId(selectedChildShape.id)] : null
    const href = scope ? atlasTaxlotsHref(scope) : null
    if (!href) {
      setSelectionTaxlots(null)
      return
    }
    const controller = new AbortController()
    void fetchJsonOnce(href, asFetchedTaxlots, controller.signal).then((got) => {
      if (!controller.signal.aborted) setSelectionTaxlots(got ?? [])
    })
    return () => controller.abort()
  }, [selectedChildShape, taxlotBoundaries])

  /* Matt 2026-09-23, requirement 5: named local streets, from the SAME
     source the roads layer already draws (lib/geo/basemap-streets.ts,
     TIGER/Line MTFCC S1400) — nothing here invents a name the source lacks.
     One label per named path, at the midpoint of its longest run, capped so
     a dense grid cannot out-render the map it sits on. */
  const namedRoadPoints = useMemo(() => {
    if (!basemap) return []
    const out: { name: string; lon: number; lat: number; length: number }[] = []
    for (const f of basemap.roads) {
      if (!f.n) continue
      for (const points of decodeBasemapFeature(f, basemap.q)) {
        if (points.length < 2) continue
        let total = 0
        const segs: { a: LonLat; b: LonLat; len: number }[] = []
        for (let i = 1; i < points.length; i += 1) {
          const a = points[i - 1]!
          const b = points[i]!
          const len = Math.hypot(b[0] - a[0], b[1] - a[1])
          segs.push({ a, b, len })
          total += len
        }
        if (total <= 0) continue
        let remaining = total / 2
        let mid: LonLat = points[0]!
        for (const seg of segs) {
          if (remaining <= seg.len) {
            const t = seg.len > 0 ? remaining / seg.len : 0
            mid = [seg.a[0] + (seg.b[0] - seg.a[0]) * t, seg.a[1] + (seg.b[1] - seg.a[1]) * t]
            break
          }
          remaining -= seg.len
        }
        out.push({ name: f.n, lon: mid[0], lat: mid[1], length: total })
      }
    }
    out.sort((a, b) => b.length - a.length)
    return out.slice(0, 90)
  }, [basemap])

  /* Matt 2026-09-23, requirement 1: the selected district's own name, at its
     interior label point — the pole of inaccessibility when the grid finds
     one inside the polygon, else the shape's own anchor, else nothing
     rather than a point that might sit outside the ring. */
  const selectedInteriorLonLat = useMemo(() => {
    if (!selectedChildShape) return null
    return polygonInteriorPoint(selectedChildShape.rings) ?? selectedChildShape.anchor ?? null
  }, [selectedChildShape])
  const selectedLabelText = selectedChildShape ? doorLabel(selectedChildShape) : ''
  const selectedLabelStyle = useMemo(() => {
    if (!view || !selectedInteriorLonLat || !selectedLabelText) return null
    const [sx, sy] = screenOf(selectedInteriorLonLat[0], selectedInteriorLonLat[1])
    // A rough half-width from the string, generous rather than measured — the
    // clamp only needs to keep the pill on stage, never clip it (the same
    // bar atlasLabelBox holds for the packed place labels).
    const halfW = Math.max(34, selectedLabelText.length * 3.9) + 14
    const halfH = 17
    return {
      left: Math.min(Math.max(sx, halfW), Math.max(halfW, view.w - halfW)),
      top: Math.min(Math.max(sy, halfH), Math.max(halfH, view.h - halfH)),
    }
  }, [view, selectedInteriorLonLat, selectedLabelText, screenOf])

  const packedLabels = useMemo(() => {
    if (!view) return []
    const candidates: AtlasLabelCandidate[] = []
    if (highlight) {
      const d = dots.find((dot) => dot.k === highlight.key)
      if (d) {
        const [x, y] = screenOf(d.lng, d.lat)
        const text = highlight.label
        candidates.push({ id: 'home', kind: 'home', text, x, y: y - 14, rank: 10_000, ...atlasLabelBox(text, 'home') })
      }
    }
    for (const s of towns) {
      if (!s.anchor || isFrame(s) || s.id === active) continue
      const text = shortPlaceLabel(s.name)
      const [x, y] = screenOf(s.anchor[0], s.anchor[1])
      candidates.push({
        id: s.id,
        kind: 'town',
        text,
        x,
        y: y + 6,
        rank: 1_000 + s.area * 1e6,
        ...atlasLabelBox(text, 'town'),
      })
    }
    if (cam.k > 1.2) {
      for (const s of places) {
        if (!s.anchor || isFrame(s) || s.id === active || !s.bbox) continue
        // SITE-128: child plats stay unlabeled until selected.
        if (childIdSet.has(s.id) && !childPaintLive(pinned?.id, s.id)) continue
        const [x0, y0] = screenOf(s.bbox.minLon, s.bbox.maxLat)
        const [x1, y1] = screenOf(s.bbox.maxLon, s.bbox.minLat)
        if (Math.min(Math.abs(x1 - x0), Math.abs(y1 - y0)) < 32) continue
        const text = doorLabel(s)
        if (!text) continue
        const [x, y] = screenOf(s.anchor[0], s.anchor[1])
        const n = regionStats.get(s.id)?.n ?? 0
        candidates.push({
          id: `pl-${s.id}`,
          kind: 'place',
          text,
          x,
          y,
          rank: 100 + n * 8 + s.area * 1e6,
          ...atlasLabelBox(text, 'place'),
        })
      }
    }
    // Matt 2026-09-23, requirement 5: street names, at street-level zoom
    // generally (not only inside a selection) — low rank, so a place or the
    // district label always wins a collision over a street running past it.
    if (cam.k > 1.8) {
      for (const r of namedRoadPoints) {
        const [x, y] = screenOf(r.lon, r.lat)
        candidates.push({
          id: `st-${r.name}-${Math.round(x)}-${Math.round(y)}`,
          kind: 'street',
          text: r.name,
          x,
          y,
          rank: 10 + Math.min(r.length, 50),
          ...atlasLabelBox(r.name, 'street'),
        })
      }
    }
    if (activeShape?.anchor && !isFrame(activeShape)) {
      if (childIdSet.has(activeShape.id) && !childPaintLive(pinned?.id, activeShape.id)) {
        return packAtlasLabels(candidates, view)
      }
      const text = doorLabel(activeShape)
      if (!text) return packAtlasLabels(candidates, view)
      const [x, y] = screenOf(activeShape.anchor[0], activeShape.anchor[1])
      candidates.push({
        id: `on-${activeShape.id}`,
        kind: 'active',
        text,
        x,
        y,
        rank: 9_000,
        ...atlasLabelBox(text, 'active'),
      })
    }
    return packAtlasLabels(candidates, view)
  }, [
    view,
    highlight,
    dots,
    towns,
    places,
    active,
    activeShape,
    cam.k,
    screenOf,
    isFrame,
    regionStats,
    doorLabel,
    childIdSet,
    pinned,
    namedRoadPoints,
  ])

  const activeHomes = useMemo(() => {
    if (!active || incomplete) return []
    const rows: { href: string; price: string; type: string }[] = []
    for (const i of counts.listed) {
      const d = dots[i]
      if (!d?.href || d.s === 'closed' || d.s === 'sold') continue
      if (!(membership[i] ?? []).includes(active)) continue
      rows.push({
        href: d.href,
        price: d.p != null ? fmtShort(d.p) : 'Price withheld',
        type: types.find((t) => t.key === d.t)?.label ?? 'Listing',
      })
      if (rows.length >= 3) break
    }
    return rows
  }, [active, incomplete, counts.listed, dots, membership, types])

  const cardAnchor = pinned
    ? ([pinned.at[0] * cam.k + cam.x, pinned.at[1] * cam.k + cam.y] as const)
    : pointer
      ? ([pointer.x, pointer.y] as const)
      : activeShape?.anchor
        ? screenOf(activeShape.anchor[0], activeShape.anchor[1])
        : null
  const cardStyle =
    cardAnchor && view
      ? {
          left: Math.min(cardAnchor[0] + 16, Math.max(0, view.w - 270)),
          top: Math.max(0, Math.min(cardAnchor[1] + 16, view.h - cardH - 8)),
        }
      : undefined

  const pinMarks = useMemo<PaintedPin[]>(() => {
    if (!view) return []
    const foldStage = clusterStageHint
      ? pickCityFoldClusterStage(
          view.w,
          clusterStageHint,
          clusterStageHintPhone ?? clusterStageHint,
        )
      : null
    const raw: {
      d: AtlasDot
      i: number
      x: number
      y: number
      label: string
      projX: number
      projY: number
    }[] = []
    dots.forEach((d, i) => {
      if (!isOn(d) || !atlasPinShouldPaint(d)) return
      const label = formatAtlasPinPrice(d.p)
      if (!label) return
      const [projX, projY] = proj.toXY(d.lng, d.lat)
      const [x, y] = screenOf(d.lng, d.lat)
      raw.push({ d, i, x, y, label, projX, projY })
    })
    /* City fold: membership is the locked fold stage × cam.k, never live
       GBR. Desktop 1112×610 stays 46 marks when measure collapses; phone
       360×285 stays 14 bubbles + 2 pills. Zoom stretches that stage so
       +1–2 dissolves. Neighborhood / community have no hint — live view. */
    const grouped = clusterPins
      ? clusterAtlasPins(
          foldStage
            ? projectPinsToFoldStage(
                raw.map((p) => ({ i: p.i, x: p.projX, y: p.projY })),
                foldStage,
                proj,
                cam.k,
              )
            : raw.map((p) => ({ i: p.i, x: p.x, y: p.y })),
          clusterCellPx,
        )
      : raw.map((p) => ({
          id: `c-${p.i}`,
          x: p.x,
          y: p.y,
          indices: [p.i],
          count: 1,
        }))
    const byIndex = new Map(raw.map((p) => [p.i, p]))
    const island = { w: view.w, h: view.h }
    const screenGroups = grouped.map((g) => {
      let sx = 0
      let sy = 0
      let n = 0
      for (const i of g.indices) {
        const src = byIndex.get(i)
        if (!src) continue
        sx += src.x
        sy += src.y
        n += 1
      }
      // Clamped to the island BEFORE the overlap merge (2026-09-25): two asks
      // near the map's edge were merged as apart, then both clamped onto the
      // same edge line, and "$769k" printed over "$749k".
      const cx = n > 0 ? sx / n : g.x
      const cy = n > 0 ? sy / n : g.y
      const at =
        g.count > 1 ? clampAtlasPinToIsland(cx, cy, island, ATLAS_CLUSTER_PILL) : clampAtlasPinToIsland(cx, cy, island)
      return { ...g, x: at.x, y: at.y }
    })
    const merged = clusterPins ? mergeOverlappingAtlasClusters(screenGroups) : screenGroups
    const out: PaintedPin[] = []
    for (const g of merged) {
      if (g.count === 1) {
        const src = byIndex.get(g.indices[0]!)
        if (src) {
          const at = clampAtlasPinToIsland(src.x, src.y, island)
          out.push({ kind: 'pin', d: src.d, i: src.i, x: at.x, y: at.y, label: src.label })
        }
        continue
      }
      const asks: number[] = []
      for (const i of g.indices) {
        const p = dots[i]?.p
        if (p != null && formatAtlasPinPrice(p)) asks.push(p)
      }
      const span = atlasClusterAskSpan(asks)
      const at = clampAtlasPinToIsland(g.x, g.y, island, ATLAS_CLUSTER_PILL)
      out.push({
        kind: 'cluster',
        id: g.id,
        x: at.x,
        y: at.y,
        count: g.count,
        indices: g.indices,
        minP: span?.min ?? 0,
        maxP: span?.max ?? 0,
        median: atlasClusterMedianAsk(asks),
      })
    }
    return out
  }, [
    view,
    dots,
    isOn,
    screenOf,
    clusterPins,
    clusterCellPx,
    clusterStageHint,
    clusterStageHintPhone,
    proj,
    cam.k,
  ])
  pinMarksRef.current = pinMarks

  /* The home under the pointer: photo + ask, not a one-line status chip. */
  const homePreview = (() => {
    if (dotHit == null || pinned || !view) return null
    const d = dots[dotHit]
    if (!d || !atlasPinShouldPaint(d)) return null
    const [sx, sy] = screenOf(d.lng, d.lat)
    const facts: string[] = []
    if (d.beds != null && Number.isFinite(d.beds)) facts.push(`${Math.round(d.beds)} bd`)
    if (d.baths != null && Number.isFinite(d.baths)) {
      facts.push(`${Number.isInteger(d.baths) ? d.baths : d.baths.toFixed(1)} ba`)
    }
    if (d.sqft != null && Number.isFinite(d.sqft)) {
      facts.push(`${Math.round(d.sqft).toLocaleString('en-US')} sqft`)
    }
    const state = d.s === 'pending' ? 'Pending' : 'For sale'
    const price = d.p != null ? formatAtlasPinPrice(d.p) : ''
    const cardW = 248
    const cardH = d.photo ? 280 : 148
    const left = Math.min(Math.max(sx, cardW / 2 + 8), Math.max(cardW / 2 + 8, view.w - cardW / 2 - 8))
    const placeBelow = sy < cardH + 28
    const top = placeBelow ? Math.min(sy + 18, Math.max(18, view.h - 12)) : Math.max(12, sy - 16)
    const body = (
      <>
        {d.photo ? (
          // Spark listing photos skip next/image (row-photo.ts).
          // eslint-disable-next-line @next/next/no-img-element
          <img
            className="v3-atlas__home-photo"
            src={d.photo}
            alt={d.street || 'Home'}
            width={248}
            height={148}
            draggable={false}
          />
        ) : (
          <span className="v3-atlas__home-photo is-empty" aria-hidden="true" />
        )}
        <span className="v3-atlas__home-body">
          <span className="v3-atlas__home-state">{state}</span>
          {price ? <span className="v3-atlas__home-price">{price}</span> : null}
          {d.street ? <span className="v3-atlas__home-street">{d.street}</span> : null}
          {facts.length > 0 ? <span className="v3-atlas__home-facts">{facts.join(' · ')}</span> : null}
        </span>
      </>
    )
    const style = { left, top }
    return d.href ? (
      <Link
        href={d.href}
        className={cn('v3-atlas__home', placeBelow && 'is-below')}
        style={style}
        data-atlas-home={d.k}
        data-atlas-home-price={price || undefined}
        aria-label={d.street ? `${d.street}, ${price || state}` : `Listing, ${price || state}`}
      >
        {body}
      </Link>
    ) : (
      <p className={cn('v3-atlas__home', placeBelow && 'is-below')} role="status" style={style} data-atlas-home={d.k}>
        {body}
      </p>
    )
  })()

  const clusterPreview = (() => {
    if (!clusterHit || pinned || !view) return null
    const mark = pinMarks.find((m) => m.kind === 'cluster' && m.id === clusterHit)
    if (!mark || mark.kind !== 'cluster') return null
    const range = mark.minP > 0 ? formatAtlasClusterRange(mark.minP, mark.maxP) : ''
    const label = `${mark.count.toLocaleString('en-US')} ${noun(mark.count)}`
    const style = {
      left: Math.min(Math.max(mark.x, 8), Math.max(8, view.w - 8)),
      top: Math.max(0, mark.y - 18),
    }
    return (
      <p className="v3-atlas__tip v3-atlas__tip--cluster" role="status" style={style} data-atlas-cluster-tip={mark.id}>
        <span className="v3-atlas__tip-state">{label}</span>
        {range ? <span className="v3-atlas__tip-price">{range}</span> : null}
      </p>
    )
  })()

  const showCard = Boolean(
    activeShape &&
      activeStats &&
      (pinned || hover) &&
      dotHit == null &&
      (!childIdSet.has(activeShape.id) || childPaintLive(pinned?.id, activeShape.id)),
  )
  const card =
    showCard && activeShape && activeStats ? (
      <div ref={cardRef} className={cn('v3-atlas__card', pinned && 'is-pinned')} role="status" style={cardStyle}>
        <button type="button" className="v3-atlas__card-close" onClick={dismiss} aria-label="Close">
          ×
        </button>
        <p className="v3-atlas__card-kind">{activeShape.kindLabel ?? KIND_LABEL[activeShape.kind]}</p>
        <p className="v3-atlas__card-name">{doorLabel(activeShape)}</p>
        {incomplete ? (
          <p className="v3-atlas__card-label">Counts unavailable right now</p>
        ) : (
          <p className="v3-atlas__card-figures">
            <span className="v3-atlas__card-n">{activeStats.n.toLocaleString('en-US')}</span>
            <span className="v3-atlas__card-label">
              {noun(activeStats.n)}
              {atCeiling ? '' : ` under ${fmtShort(maxPrice)}`}
            </span>
            {activeStats.median != null ? (
              <>
                <span className="v3-atlas__card-n">{fmtShort(activeStats.median)}</span>
                <span className="v3-atlas__card-label">{medianScope.label}</span>
              </>
            ) : null}
          </p>
        )}
        {activeHomes.length > 0 ? (
          <ul className="v3-atlas__card-homes">
            {activeHomes.map((h) => (
              <li key={h.href}>
                {pinned ? (
                  <Link href={h.href} className="v3-atlas__card-home">
                    <span className="v3-atlas__card-home-price">{h.price}</span>
                    <span className="v3-atlas__card-home-type">{h.type}</span>
                  </Link>
                ) : (
                  <span className="v3-atlas__card-home">
                    <span className="v3-atlas__card-home-price">{h.price}</span>
                    <span className="v3-atlas__card-home-type">{h.type}</span>
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
        <Link href={activeShape.href} className="v3-atlas__card-door">
          {/* A long plat name is already the card's heading; repeating all 64
              characters here printed it twice, once clipped and once one word
              per line (evaluator round five, LISTING-BEND-3). */}
          {activeShape.name.length > 28
            ? `See this ${(activeShape.kindLabel ?? KIND_LABEL[activeShape.kind]).toLowerCase()}`
            : `See ${activeShape.name}`}
        </Link>
      </div>
    ) : null

  /* The key: one entry per state the map actually draws, with the mark it
     draws it in. A reader matches a dot to a word without guessing. */
  const keyItems = useMemo(() => {
    if (incomplete) return []
    if (closingsMap) {
      return counts.closed > 0
        ? [{ kind: 'closed', label: `${counts.closed.toLocaleString('en-US')} ${noun(counts.closed)}` }]
        : []
    }
    const out: { kind: string; label: string }[] = []
    if (counts.forSale > 0) {
      // W1: with keyBasis the figure says what it counts, from the same
      // toggles and ceiling the marks are filtered by (mapListingCount).
      out.push({
        kind: 'active',
        label: keyBasis
          ? forSaleText(
              mapListingCount(counts, {
                typesOn,
                types,
                maxPriceLabel: atCeiling ? null : fmtShort(maxPrice),
              }),
            )
          : `${counts.forSale.toLocaleString('en-US')} for sale`,
      })
    }
    // The under-contract marks are named, not counted: the homes under the map
    // count the part of them still showing, and two different "under
    // contract" figures on one page read as a contradiction (2026-09-29: "216
    // under contract" on the map over "3 under contract" in the homes).
    if (counts.pending > 0) out.push({ kind: 'pending', label: 'under contract' })
    if (amenityParks.length > 0) {
      out.push({
        kind: 'park',
        label: `${amenityParks.length.toLocaleString('en-US')} ${amenityParks.length === 1 ? 'park' : 'parks'}`,
      })
    }
    if (amenityTrails.length > 0) {
      out.push({
        kind: 'trail',
        label: `${amenityTrails.length.toLocaleString('en-US')} ${amenityTrails.length === 1 ? 'trail' : 'trails'}`,
      })
    }
    return out
  }, [
    counts,
    closingsMap,
    noun,
    incomplete,
    amenityParks.length,
    amenityTrails.length,
    keyBasis,
    typesOn,
    types,
    atCeiling,
    maxPrice,
  ])

  /* The key, rendered once. `keyPlacement` decides which slot holds it; the
     markup is identical in both, so a reader and a test see one list. */
  const keyList =
    keyItems.length > 0 ? (
      <ul className="v3-atlas__key" aria-label="What the marks mean">
        {keyItems.map((k) => (
          <li key={k.kind} className="v3-atlas__key-item">
            <span className={`v3-atlas__key-mark v3-atlas__key-mark--${k.kind}`} aria-hidden="true" />
            {k.label}
          </li>
        ))}
      </ul>
    ) : null

  const dock = (
    <div className="v3-atlas__dock">
      {heat.cells.length > 0 ? (
        <div
          className="v3-atlas__sales-legend"
          role="img"
          aria-label={`Sales heat, fewer sales to more sales, ${atlasHeatWindowLabel(ATLAS_HEAT_WINDOW_DAYS)}.`}
        >
          <span className="v3-atlas__sales-legend-end">fewer sales</span>
          <ol className="v3-atlas__sales-swatches" aria-hidden="true">
            {([1, 2, 3, 4] as const).map((step) => (
              <li key={step} className={`v3-atlas__sales-swatch v3-atlas__sales-swatch--${step}`} />
            ))}
          </ol>
          <span className="v3-atlas__sales-legend-end">more sales</span>
          <p className="v3-atlas__sales-legend-window">{atlasHeatWindowLabel(ATLAS_HEAT_WINDOW_DAYS)}</p>
        </div>
      ) : null}
      {keyPlacement === 'dock' ? keyList : null}
      {pinMarks.some((m) => m.kind === 'cluster') ? (
        // What the two price faces mean (2026-09-25: "median $1.2M" beside a
        // bare "$479k" read as one scale): a bubble is the middle ask of the
        // homes grouped under it, a plain tag one home's own ask.
        <p className="v3-atlas__pin-legend">
          <span className="v3-atlas__pin-legend-mark v3-atlas__pin-legend-mark--cluster" aria-hidden="true">
            homes
          </span>
          how many homes are grouped there, over their {ATLAS_CLUSTER_PIN_LABEL} ask
          {pinMarks.some((m) => m.kind === 'pin') ? (
            <>
              <span className="v3-atlas__pin-legend-mark" aria-hidden="true">
                $
              </span>
              one home&rsquo;s ask
            </>
          ) : null}
        </p>
      ) : null}
      {amenityParks.length > 0 || amenityTrails.length > 0 ? (
        <div className="v3-atlas__amenities" role="group" aria-label="Parks and trails">
          {amenityParks.length > 0 ? (
            <button
              type="button"
              className="v3-atlas__type"
              aria-pressed={showParks}
              onClick={() => setShowParks((on) => !on)}
            >
              <span className="v3-atlas__type-mark v3-atlas__type-mark--park" aria-hidden="true" />
              Parks
            </button>
          ) : null}
          {amenityTrails.length > 0 ? (
            <button
              type="button"
              className="v3-atlas__type"
              aria-pressed={showTrails}
              onClick={() => setShowTrails((on) => !on)}
            >
              <span className="v3-atlas__type-mark v3-atlas__type-mark--trail" aria-hidden="true" />
              Trails
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="v3-atlas__types" role="group" aria-label="Property types">
        {types.map((t) => (
          <button
            key={t.key}
            type="button"
            className="v3-atlas__type"
            aria-pressed={!offTypes.has(t.key)}
            /* UXLIVE-3: a filtered count needs the dots; until they land the
               toggles hold, so no count on screen describes a set the reader
               has already changed. */
            disabled={waiting}
            onClick={() => toggleType(t.key)}
          >
            <span className={`v3-atlas__type-mark v3-atlas__type-mark--${t.key}`} aria-hidden="true" />
            {t.label}
          </button>
        ))}
      </div>
      {hidePriceScrubber ? null : (
        <label className="v3-atlas__scrub">
          <span className="v3-atlas__scrub-label">
            Up to <strong className="v3-atlas__scrub-value">{atCeiling ? 'any price' : fmtShort(maxPrice)}</strong>
          </span>
          {priceBins && !waiting && priceBins.kind === 'bins' ? (
            <span className="v3-atlas__hist" aria-hidden="true">
              {priceBins.bins.map((n, i) => {
                const most = Math.max(...priceBins.bins)
                const low = priceScale.min + ((priceScale.max - priceScale.min) * i) / PRICE_BINS
                return (
                  <span
                    key={i}
                    className={cn('v3-atlas__hist-bar', !atCeiling && low > maxPrice && 'is-out')}
                    style={{ height: `${Math.max(n > 0 ? 8 : 0, (n / most) * 100).toFixed(1)}%` }}
                  />
                )
              })}
            </span>
          ) : null}
          {priceBins && !waiting && priceBins.kind === 'ticks' ? (
            <span className="v3-atlas__hist v3-atlas__hist--ticks" aria-hidden="true">
              {priceBins.xs.map((x, i) => (
                <span
                  key={i}
                  className={cn(
                    'v3-atlas__hist-tick',
                    !atCeiling && priceScale.min + (priceScale.max - priceScale.min) * x > maxPrice && 'is-out',
                  )}
                  style={{ left: `calc(0.5rem + ${x.toFixed(4)} * (100% - 1rem))` }}
                />
              ))}
            </span>
          ) : null}
          <input
            className="v3-atlas__range"
            type="range"
            min={priceScale.min}
            max={priceScale.max}
            step={priceScale.step}
            value={maxPrice}
            disabled={waiting}
            onChange={(e) => setMaxPrice(Number(e.target.value))}
            aria-valuetext={atCeiling ? 'Any price' : `Up to ${fmtShort(maxPrice)}`}
          />
        </label>
      )}
    </div>
  )

  return (
    <section
      ref={sectionRef}
      id={id}
      className={cn(
        V3_ROOT_CLASS,
        'v3-atlas',
        pairedSelection && 'v3-atlas--paired',
        subjectGrain && 'v3-atlas--subject-grain',
        wide && 'is-wide',
        fitsPhone && 'is-fits',
        !inView && 'is-offscreen',
        className,
      )}
      data-map-hierarchy={subjectGrain ? 'subject-grain' : 'place'}
      /* UXLIVE-3: whether the marks are on the map yet (deferred maps only). */
      data-atlas-dots={deferDots ? (waiting ? (dotsFailed ? 'failed' : 'loading') : 'ready') : undefined}
      aria-busy={waiting && !dotsFailed ? true : undefined}
      aria-labelledby={`${uid}-h`}
      /* The linked mark's state, on the section, so a sibling list and a test
         can both read what the map is pointing at without walking the SVG. */
      data-atlas-linked={linkedIndex != null ? (linkedKey ?? undefined) : undefined}
    >
      <div className="v3-atlas__grid">
        {/* The head: the H1 and the claim. On a phone the map follows at once. */}
        <div className="v3-atlas__head">
          <Heading
            id={`${uid}-h`}
            className={cn(
              'v3-atlas__headline',
              headingLevel === 1 && headlineTone === 'display' && 'v3-atlas__headline--h1',
              headlineTone === 'eyebrow' && 'v3-atlas__headline--eyebrow',
            )}
          >
            {headline}
          </Heading>
          {quiet || pairedSelection || !claim ? null : (
            <p className="v3-atlas__claim" aria-live="polite">
              {claim}
            </p>
          )}
          {/* The key in the head (SITE-47, opt-in): the encoding sits with the
              claim, above the map, so the first read lands without a hover and
              without the reader finding the dock below a tall frame. */}
          {quiet || keyPlacement !== 'head' ? null : keyList}
        </div>

        <div className="v3-atlas__body">
          <div className="v3-atlas__frame">
            <div className="v3-atlas__zoom" role="group" aria-label="Map zoom">
              <button
                type="button"
                className="v3-atlas__zoom-btn"
                aria-label="Zoom in"
                disabled={cam.k >= ATLAS_K_MAX - 1e-6}
                onClick={() => zoomBy(1.18)}
              >
                +
              </button>
              <button
                type="button"
                className="v3-atlas__zoom-btn"
                aria-label="Zoom out"
                disabled={cam.k <= ATLAS_K_MIN + 1e-6}
                onClick={() => zoomBy(1 / 1.18)}
              >
                −
              </button>
            </div>
            <div
              ref={stageRef}
              className="v3-atlas__stage"
              /* The box IS the painting: with only a height clamp, a portrait
                 frame painted 71% of a full-width box and left the rest tinted
                 cream (evaluator round five, LISTING-NOBOUNDARY-4). */
              style={{
                aspectRatio: `${proj.width} / ${proj.height}`,
                ['--atlas-aspect' as string]: `${(proj.width / proj.height).toFixed(4)}`,
              }}
              onPointerMove={onMove}
              /* Drag pans. A tap that did not move opens the listing under
                 the mark, or the place. Wheel zoom is bound in an effect so
                 it can preventDefault. */
              onPointerDown={(e) => {
                if (e.button !== 0) return
                const at = pointerOnStage(e)
                if (!at) return
                // A user-initiated drag/pinch always wins over an in-flight
                // fit transition, so direct manipulation never feels laggy.
                setFitting(false)
                ptsRef.current.set(e.pointerId, { x: at.px, y: at.py })
                e.currentTarget.setPointerCapture(e.pointerId)
                if (ptsRef.current.size >= 2) {
                  const pts = [...ptsRef.current.values()]
                  const a = pts[0]
                  const b = pts[1]
                  pinchRef.current = a && b ? { d: Math.hypot(a.x - b.x, a.y - b.y) } : null
                  dragRef.current = { x: at.px, y: at.py, moved: true }
                  return
                }
                dragRef.current = { x: at.px, y: at.py, moved: false }
                const [wx, wy] = screenToWorld(camRef.current, at.px, at.py)
                setPointer({ x: at.px, y: at.py })
                applyLayerHit(at.px, at.py, wx, wy)
              }}
              onPointerUp={(e) => {
                ptsRef.current.delete(e.pointerId)
                if (ptsRef.current.size < 2) pinchRef.current = null
                const drag = dragRef.current
                dragRef.current = ptsRef.current.size > 0 ? drag : null
                if (drag?.moved) return
                const at = pointerOnStage(e)
                if (!at) return
                const [wx, wy] = screenToWorld(camRef.current, at.px, at.py)
                const hit = applyLayerHit(at.px, at.py, wx, wy)
                if (!hit) return
                switch (hit.kind) {
                  case 'cluster': {
                    const mark = pinMarksRef.current.find((m) => m.kind === 'cluster' && m.id === hit.id)
                    if (mark?.kind === 'cluster') zoomIntoCluster(mark.indices)
                    return
                  }
                  case 'pin': {
                    const d = dots[hit.i]
                    if (d?.href) router.push(d.href)
                    return
                  }
                  default: {
                    const _never: never = hit
                    return _never
                  }
                }
              }}
              onPointerLeave={(e) => {
                if (dragRef.current) return
                if (e.pointerType !== 'mouse') return
                setHover(null)
                setPointer(null)
                setDotHit(null)
                setClusterHit(null)
              }}
            >
              <div
                className={cn('v3-atlas__world', fitting && 'is-fitting')}
                style={{
                  transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.k})`,
                  transformOrigin: '0 0',
                }}
              >
              <svg
                className="v3-atlas__svg"
                viewBox={`0 0 ${proj.width} ${proj.height}`}
                preserveAspectRatio="xMidYMid meet"
                role="img"
                aria-label={
                  /* The map's name counts every mark it draws. It used to name
                     the for-sale figure alone over a map holding three
                     (evaluator round five, LISTING-BEND-11). */
                  /* The held home is the one thing a listing page's map adds
                     over the neighborhood's own, and the label naming it is
                     aria-hidden, so the map's own name carries it (evaluator
                     round five, LISTING-BEND-4). */
                  incomplete
                    ? 'Map of listings'
                    : `Map. ${claim}${highlight && dots.some((d) => d.k === highlight.key) ? ` ${highlight.label} is marked.` : ''}`
                }
              >
                {/* 0. The basemap: the highway skeleton, the named rivers and
                    the lakes, drawn in the register under everything so a
                    reader can place a dot without a foreign tile layer. */}
                {basemapPaths ? (
                  <g className="v3-atlas__basemap" aria-hidden="true">
                    {basemapPaths.bodies.map((p, i) => (
                      <path key={`w-${i}`} d={p.d} className={`v3-atlas__water v3-atlas__water--${p.cls}`}>
                        {p.name ? <title>{p.name}</title> : null}
                      </path>
                    ))}
                    {basemapPaths.waterways.map((p, i) => (
                      <path key={`s-${i}`} d={p.d} className={`v3-atlas__stream v3-atlas__stream--${p.cls}`}>
                        {p.name ? <title>{p.name}</title> : null}
                      </path>
                    ))}
                    {basemapPaths.roads.map((p, i) => (
                      <path key={`r-${i}`} d={p.d} className={`v3-atlas__road v3-atlas__road--${p.cls}`}>
                        {p.name ? <title>{p.name}</title> : null}
                      </path>
                    ))}
                  </g>
                ) : null}
                {showParks && amenityParkPaths.length > 0 ? (
                  <g className="v3-atlas__amenity-parks" data-atlas-amenity-layer="parks" aria-hidden="true">
                    {amenityParkPaths.map((park) => (
                      <path
                        key={park.id}
                        d={park.d}
                        className="v3-atlas__amenity-park"
                        data-atlas-amenity="park"
                      >
                        <title>{park.name}</title>
                      </path>
                    ))}
                  </g>
                ) : null}
                {showTrails && amenityTrailPaths.length > 0 ? (
                  <g className="v3-atlas__amenity-trails" data-atlas-amenity-layer="trails" aria-hidden="true">
                    {amenityTrailPaths.map((trail) => (
                      <path
                        key={trail.id}
                        d={trail.d}
                        className="v3-atlas__amenity-trail"
                        data-atlas-amenity="trail"
                      >
                        <title>{trail.name}</title>
                      </path>
                    ))}
                  </g>
                ) : null}
                <g className="v3-atlas__towns">
                  {towns.map((s) => (
                    <path
                      key={s.id}
                      d={s.d}
                      className={cn('v3-atlas__town', active === s.id && 'is-active')}
                      data-map-hierarchy="subject"
                      onPointerEnter={() => setHover(s.id)}
                      onClick={(e) => {
                        if (dotHit != null && dots[dotHit]?.href) {
                          e.stopPropagation()
                          return
                        }
                        openPlace(s)
                      }}
                    />
                  ))}
                </g>
                {/* Sales wash: kernel density of closings. Under the inventory
                    marks and the place outlines. Not the old inventory fog. */}
                {heat.cells.length > 0 && cam.k <= 1.35 ? (
                  <g className="v3-atlas__sales-heat" aria-hidden="true">
                    {heat.cells.map((c, i) => (
                      <rect
                        key={`h-${c.x}-${c.y}-${i}`}
                        x={c.x}
                        y={c.y}
                        width={c.size}
                        height={c.size}
                        className={`v3-atlas__sales-cell v3-atlas__sales-cell--${c.step}`}
                      />
                    ))}
                  </g>
                ) : null}
                {/* 2. Dots: inventory, UNDER the places so no outline is
                    ever painted over (pass four, Q1). Sold closes are the
                    wash, not a second mark, except on a record map. */}
                {(
                  <g className="v3-atlas__dots" aria-hidden="true">
                    {dots.map((d, i) => {
                      if (d.s === 'sold' && !closingsMap && d.k !== highlight?.key) return null
                      /* SITE-128: pin-eligible asks are HTML pills / bubbles.
                         Leaving the SVG dots painted 759 silent marks that
                         Look counted as pills on /cities/bend. */
                      if (atlasPinShouldPaint(d)) return null
                      const [x, y] = xy[i]!
                      return (
                        <path
                          key={d.k}
                          d={`M${x.toFixed(1)} ${y.toFixed(1)}h0`}
                          className={cn(
                            'v3-atlas__dot',
                            `v3-atlas__dot--${d.t}`,
                            d.s === 'pending' && 'v3-atlas__dot--pending',
                            d.s === 'sold' && 'v3-atlas__dot--sold',
                            d.s === 'closed' && 'v3-atlas__dot--closed',
                            highlight && d.k === highlight.key && 'is-home',
                            !isOn(d) && 'is-off',
                            active && !inActivePlace(i) && 'is-away',
                          )}
                        />
                      )
                    })}
                  </g>
                )}
                {/* 3. Hit clones UNDER the places: a wide transparent edge that
                    takes the taps landing in the gaps between fills on a phone.
                    An interior tap reaches the place itself first (R1). */}
                <g className="v3-atlas__hits" aria-hidden="true">
                  {drawnPlaces.map((s, i) =>
                    /* A place that is not a door has no hit edge either: a
                       thumb must not open what the keyboard cannot reach. */
                    doors.has(s.id) ? (
                      <use
                        key={`t-${s.id}`}
                        href={`#${uid}-p-${i}`}
                        className="v3-atlas__hit"
                        /* A `use` clones a focusable path, so the clone becomes a
                           tab stop of its own — twenty-seven silent, invisible
                           stops before the keyboard reached a real door
                           (evaluator round five, HOMEPAGE-1). The clones are
                           pointer targets and nothing else. */
                        focusable="false"
                        tabIndex={-1}
                        onPointerEnter={() => setHover(s.id)}
                        onClick={(e) => {
                          if (dotHit != null && dots[dotHit]?.href) {
                            e.stopPropagation()
                            return
                          }
                          openPlace(s)
                        }}
                      />
                    ) : null,
                  )}
                </g>
                {/* 4. Halos: the same places cloned in cream, so every outline
                    reads over the field. */}
                <g className="v3-atlas__halos" aria-hidden="true">
                  {drawnPlaces.map((s, i) => (
                    <use key={`h-${s.id}`} href={`#${uid}-p-${i}`} focusable="false" tabIndex={-1} />
                  ))}
                </g>
                {/* 5. Places: the doors, and the one copy of every path. A place
                    with nothing on it stays a door but wears less ink. A place
                    with no full-size partner on the page (`doors`, above) is
                    drawn but is not a control: no role, no tab stop, no name,
                    no pointer. */}
                <g className="v3-atlas__places" ref={placesRef}>
                  {drawnPlaces.map((s, i) => {
                    const door = doors.get(s.id)
                    return (
                      <path
                        key={s.id}
                        id={`${uid}-p-${i}`}
                        d={s.d}
                        className={cn(
                          'v3-atlas__place',
                          `v3-atlas__place--${s.kind}`,
                          childIdSet.has(s.id) && 'v3-atlas__place--child',
                          !door && 'v3-atlas__place--inert',
                          (regionStats.get(s.id)?.n ?? 0) === 0 && 'is-empty',
                          active === s.id && 'is-active',
                          selectedChildShape?.id === s.id && 'is-selected',
                        )}
                        data-map-hierarchy={
                          childIdSet.has(s.id)
                            ? active === s.id
                              ? 'selected-child'
                              : 'child-hit'
                            : undefined
                        }
                        {...(door
                          ? {
                              /* One tab stop for the whole map; arrows walk the
                                 doors. Twenty-seven stops here plus twenty-four
                                 chips below put fifty-one presses between the
                                 chrome and the search box (evaluator round five,
                                 HOMEPAGE-1). */
                              tabIndex: i === rovingDoor ? 0 : -1,
                              role: 'button',
                              /* The partner's own name (the chip's door label,
                                 or the rail row's name on a paired map), so the
                                 two pair as one control. */
                              'aria-label': door,
                              onFocus: () => {
                                setHover(s.id)
                                setRoving(i)
                              },
                              onKeyDown: (e: ReactKeyboardEvent<SVGPathElement>) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  openPlace(s)
                                  return
                                }
                                const move =
                                  e.key === 'ArrowRight' || e.key === 'ArrowDown'
                                    ? 'next'
                                    : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
                                      ? 'prev'
                                      : e.key === 'Home'
                                        ? 'first'
                                        : e.key === 'End'
                                          ? 'last'
                                          : null
                                if (!move) return
                                e.preventDefault()
                                const next = atlasNextDoor(doorIndices, i, move)
                                if (next < 0) return
                                setRoving(next)
                                placesRef.current?.querySelectorAll<SVGPathElement>('.v3-atlas__place')[next]?.focus()
                              },
                              onPointerEnter: () => setHover(s.id),
                              onClick: (e: ReactMouseEvent<SVGPathElement>) => {
                                if (dotHit != null && dots[dotHit]?.href) {
                                  e.stopPropagation()
                                  return
                                }
                                openPlace(s)
                              },
                            }
                          : {})}
                      />
                    )
                  })}
                </g>
                {/* Homes in the hovered/pinned place, painted ON TOP of the
                    fill so the inventory that belongs here is the thing you
                    see, not a wash of every other mark. */}
                {active ? (
                  <g className="v3-atlas__dots v3-atlas__dots--here" aria-hidden="true">
                    {dots.map((d, i) => {
                      if (!isOn(d) || !inActivePlace(i)) return null
                      if (d.s === 'sold' && !closingsMap && d.k !== highlight?.key) return null
                      if (atlasPinShouldPaint(d)) return null
                      const [x, y] = xy[i]!
                      return (
                        <path
                          key={`here-${d.k}`}
                          d={`M${x.toFixed(1)} ${y.toFixed(1)}h0`}
                          className={cn(
                            'v3-atlas__dot',
                            'is-here',
                            `v3-atlas__dot--${d.t}`,
                            d.s === 'pending' && 'v3-atlas__dot--pending',
                            d.s === 'sold' && 'v3-atlas__dot--sold',
                            d.s === 'closed' && 'v3-atlas__dot--closed',
                            highlight && d.k === highlight.key && 'is-home',
                          )}
                        />
                      )
                    })}
                  </g>
                ) : null}
                {/* 5b. The linked mark: the one listing a sibling list is
                    pointing at, ringed so the row and the map are visibly the
                    same object. Drawn as its own layer rather than a class on
                    the dot so a hover costs one path, not 1,500. */}
                {linkedIndex != null && xy[linkedIndex] ? (
                  <g
                    className="v3-atlas__linked"
                    aria-hidden="true"
                    data-atlas-linked-key={linkedKey ?? undefined}
                  >
                    <path
                      d={`M${xy[linkedIndex]![0].toFixed(1)} ${xy[linkedIndex]![1].toFixed(1)}h0`}
                      className="v3-atlas__linked-halo"
                    />
                    <path
                      d={`M${xy[linkedIndex]![0].toFixed(1)} ${xy[linkedIndex]![1].toFixed(1)}h0`}
                      className="v3-atlas__linked-mark"
                    />
                  </g>
                ) : null}
                {/* 6. Lot lines. The assessor's recorded shape of a parcel:
                    the subject in full ink, its neighbours as hairlines, and
                    a disclaimer beside the map because this is not a survey. */}
                {parcelPaths.length > 0 ? (
                  <g className="v3-atlas__parcels" aria-hidden="true">
                    {parcelPaths.map((p) => (
                      <path
                        key={`lot-${p.id}`}
                        d={p.d}
                        className={cn('v3-atlas__parcel', p.subject && 'is-subject')}
                      >
                        {p.name ? <title>{p.name}</title> : null}
                      </path>
                    ))}
                  </g>
                ) : null}
              </svg>

              {/* Pulses: their own layer, slots per kind, paused off-screen. */}
              <svg
                className="v3-atlas__pulses"
                viewBox={`0 0 ${proj.width} ${proj.height}`}
                preserveAspectRatio="xMidYMid meet"
                aria-hidden="true"
              >
                {pulses.map(({ i, kind }, n) => {
                  const [x, y] = xy[i]!
                  return (
                    <path
                      key={`p-${dots[i]!.k}`}
                      d={`M${x.toFixed(1)} ${y.toFixed(1)}h0`}
                      className={`v3-atlas__pulse v3-atlas__pulse--${kind}`}
                      style={{ animationDelay: `${(n % 13) * 190}ms` }}
                    />
                  )
                })}
              </svg>
              </div>

              {/* Names sit in SCREEN space, outside the camera transform, so
                  zoom does not stack type on itself. Packed so one name
                  occupies a point. */}
              {view && packedLabels.length > 0 ? (
                <div className="v3-atlas__labels" aria-hidden="true">
                  {packedLabels.map((l) => (
                    <span
                      key={l.id}
                      className={cn(
                        'v3-atlas__label',
                        l.kind === 'home' && 'v3-atlas__label--home',
                        l.kind === 'place' && 'v3-atlas__label--place',
                        l.kind === 'active' && 'v3-atlas__label--active',
                        l.kind === 'street' && 'v3-atlas__label--street',
                      )}
                      style={{ left: l.x, top: l.y }}
                    >
                      {l.text}
                    </span>
                  ))}
                </div>
              ) : null}

              {view && pinMarks.length > 0 ? (
                <div
                  className="v3-atlas__pins"
                  aria-hidden="true"
                  data-atlas-pin-layer={clusterPins ? 'clustered' : 'pills'}
                  data-atlas-cluster-cell={clusterPins ? clusterCellPx : undefined}
                  data-atlas-cluster-stage={
                    clusterStageHint
                      ? view.w >= CITY_FOLD_CLUSTER_BREAKPOINT_PX
                        ? 'desktop'
                        : 'phone'
                      : undefined
                  }
                >
                  {pinMarks.map((mark) => {
                    switch (mark.kind) {
                      case 'cluster': {
                        /* UXLIVE-6: the MEDIAN ask of the homes in the
                           bubble, labelled, never the cheapest with "+"
                           ("$50k+" over Bend read as a wrong price). */
                        const price = formatAtlasPinPrice(mark.median)
                        return (
                          <span
                            key={mark.id}
                            className={cn(
                              'v3-atlas__pin',
                              'is-cluster',
                              clusterHit === mark.id && 'is-hot',
                              // The linked home sits inside this bubble: ring
                              // the bubble, or the ring under it never shows
                              // (2026-09-25, the type page's dial and map).
                              linkedIndex != null && mark.indices.includes(linkedIndex) && 'is-linked',
                            )}
                            style={
                              {
                                ['--atlas-pin-x']: `${mark.x}px`,
                                ['--atlas-pin-y']: `${mark.y}px`,
                              } as CSSProperties
                            }
                            data-atlas-cluster={mark.id}
                            data-atlas-cluster-n={mark.count}
                            data-atlas-cluster-price={price || undefined}
                            data-atlas-cluster-size={atlasClusterSize(mark.count)}
                          >
                            {price ? (
                              <>
                                {/* How many homes the bubble holds over their median
                                    ask (2026-09-29: two dozen pills all reading
                                    "median" said nothing apart; the key says the
                                    figure is the median). */}
                                <span className="v3-atlas__pin-kind">
                                  {mark.count.toLocaleString('en-US')} {mark.count === 1 ? 'home' : 'homes'}
                                </span>
                                <span className="v3-atlas__pin-ask">{price}</span>
                              </>
                            ) : null}
                          </span>
                        )
                      }
                      case 'pin': {
                        const { d, i, x, y, label } = mark
                        return (
                          <span
                            key={d.k}
                            className={cn(
                              'v3-atlas__pin',
                              d.s === 'pending' && 'is-pending',
                              highlight && d.k === highlight.key && 'is-home',
                              linkedIndex === i && 'is-linked',
                              dotHit === i && 'is-hot',
                            )}
                            style={
                              {
                                ['--atlas-pin-x']: `${x}px`,
                                ['--atlas-pin-y']: `${y}px`,
                              } as CSSProperties
                            }
                            data-atlas-pin={d.k}
                            data-atlas-pin-price={label}
                          >
                            {label}
                          </span>
                        )
                      }
                      default: {
                        const _never: never = mark
                        return _never
                      }
                    }
                  })}
                </div>
              ) : null}

              {clusterPreview}
              {homePreview}
              {/* Matt 2026-09-23, requirement 1: the selected district's own
                  name, at its interior label point, above every pill
                  (z-index) and clamped in JS so the stage's overflow:hidden
                  can never clip it. Deselecting removes the shape it names,
                  which removes this. */}
              {selectedChildShape && selectedLabelStyle && selectedLabelText ? (
                <span className="v3-atlas__district-label" style={selectedLabelStyle} aria-hidden="true">
                  {selectedLabelText}
                </span>
              ) : null}
            </div>
            {/* The card sits in the frame, not the stage: on a phone it drops
                below the map in flow, because pinned over a 208px stage it
                covered the map it belongs to on 14 of 20 taps (evaluator
                round five, LISTING-BEND-1). */}
            {card}
          </div>

        </div>

        {/* Matt 2026-09-23, requirement 6: the assessor disclaimer beside the
            map, exactly as lib/data/geo/getTaxlots.ts requires of anyone who
            draws one of these lines (CLAUDE.md §0) — only for the lots THIS
            component fetched; a caller passing its own `parcels` prop
            already prints its own copy of this note elsewhere on its page. */}
        {selectionTaxlots && selectionTaxlots.length > 0 ? (
          <p className="v3-atlas__taxlot-note">{TAXLOT_DISCLAIMER}</p>
        ) : null}

        {/* The legend: type toggles and the price scrubber. Under the map on a
            phone; under the head in the desktop column. */}
        {quiet ? null : dock}

        {/* The aside: the live line, the search, the source. */}
        {pairedSelection ? null : <div className="v3-atlas__aside">
          {events && events.length > 0 ? (
            <ul className="v3-atlas__live" aria-label="Latest activity">
              {events.slice(0, 3).map((e) => (
                <li key={e.key} className={`v3-atlas__live-item v3-atlas__live-item--${e.kind}`}>
                  <Link href={e.href} className="v3-atlas__live-link">
                    {e.label}
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
          {children ? <div className="v3-atlas__search">{children}</div> : null}
          {/* SITE-159: this is the map's reach list, not a second map. Cluster
              pins paint under 20px; these names are the only thumb-reachable
              path onto a place. A visible label + claim + a section rule are
              the change of surface; the chips stay in flow (never a
              disclosure, never under the tap floor). After search so a
              search field is not pushed off the column (TEAM-MATT-5). */}
          {chipPlaces.length > 0 ? (
            <div className="v3-atlas__reach">
              <h3 className="v3-atlas__reach-label" id={`${uid}-reach`}>
                Reach list
              </h3>
              <p className="v3-atlas__reach-claim" id={`${uid}-reach-claim`}>
                {chipPlaces.length.toLocaleString('en-US')}{' '}
                {chipPlaces.length === 1 ? 'place' : 'places'} on this map.
                {/* W1 (audit v2 2.1: /about's Sunriver chip read 129 beside 116
                    on its own page, unlabeled): a chip counts the marks inside
                    the place's shape, for sale and under contract, so the
                    claim says so, with the map's own basis. */}
                {closingsMap ? null : ` ${reachChipBasisText(filterBasis)}`}
              </p>
              {/* Wrapped at every width; a set past the fold shows its first
                 chips and one trailing "+ N more" chip that opens the rest
                 (aria-expanded, aria-controls). The rest is two wrappers so the
                 stylesheet can fold at eight on a phone and at twenty-four from
                 48rem with one control; the chip carries both counts and shows
                 the one its width means. Every chip reads the door label, so
                 siblings that differ only by phase stay distinct and no chip is
                 ever clipped at the viewport edge (SITE-07, 2026-09-09). */}
              <div
                className="v3-atlas__chips"
                role="group"
                aria-labelledby={`${uid}-reach`}
                aria-describedby={`${uid}-reach-claim`}
              >
                {chipPlaces.slice(0, CHIP_FOLD_AT).map(chipButton)}
                {chipPlaces.length > CHIP_FOLD_AT ? (
                  <>
                    <div
                      id={`${uid}-chips-rest`}
                      className={cn('v3-atlas__chips-rest', !chipsOpen && 'is-folded')}
                    >
                      {chipPlaces.slice(CHIP_FOLD_AT, CHIP_FOLD_AT_WIDE).map(chipButton)}
                    </div>
                    {chipPlaces.length > CHIP_FOLD_AT_WIDE ? (
                      <div
                        id={`${uid}-chips-rest-wide`}
                        className={cn('v3-atlas__chips-rest-wide', !chipsOpen && 'is-folded')}
                      >
                        {chipPlaces.slice(CHIP_FOLD_AT_WIDE).map(chipButton)}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className={cn(
                        'v3-atlas__chip v3-atlas__chip--more',
                        chipPlaces.length <= CHIP_FOLD_AT_WIDE && 'v3-atlas__chip--more-narrow-only',
                      )}
                      aria-expanded={chipsOpen}
                      aria-controls={
                        chipPlaces.length > CHIP_FOLD_AT_WIDE
                          ? `${uid}-chips-rest ${uid}-chips-rest-wide`
                          : `${uid}-chips-rest`
                      }
                      onClick={() => setChipsOpen((open) => !open)}
                    >
                      {/* Two labels, one shown per width (display: none keeps the
                          hidden one out of the accessible name). */}
                      <span className="v3-atlas__chip-name v3-atlas__chip-name--narrow">
                        {chipsOpen ? 'Fewer' : `+ ${chipPlaces.length - CHIP_FOLD_AT} more`}
                      </span>
                      {/* Only when the wide fold has a rest: below it this read "+ -15 more". */}
                      {chipPlaces.length > CHIP_FOLD_AT_WIDE ? (
                        <span className="v3-atlas__chip-name v3-atlas__chip-name--wide">
                          {chipsOpen ? 'Fewer' : `+ ${chipPlaces.length - CHIP_FOLD_AT_WIDE} more`}
                        </span>
                      ) : null}
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}
          {source ? (
            <details className="v3-atlas__source">
              <summary className="v3-atlas__source-summary">Source{stamp ? ` · updated ${stamp}` : ''}</summary>
              <p className="v3-atlas__source-body">
                {source}
                {drawnCount > 1
                  ? outlinedOf != null && outlinedOf > drawnCount
                    ? ` The map outlines ${drawnCount} of the ${outlinedOf.toLocaleString('en-US')} places with a recorded boundary here; listings outside an outline are counted and drawn as dots with no outline to tap.`
                    : ` The map outlines the ${drawnCount} places with a recorded boundary; listings outside them are counted and drawn as dots with no outline to tap.`
                  : ''}
                {beyond.n > 0
                  ? ` ${beyond.n} of the ${beyond.on.toLocaleString('en-US')} ${beyond.n === 1 ? 'sits' : 'sit'} beyond the frame's edges: counted in every figure, not drawn.`
                  : ''}
                {incomplete ? ' A read failed on this render, so no count is printed.' : ''}
                {waiting && dotsFailed && !incomplete
                  ? ' The homes did not load onto the map in this browser. The counts come from the same listing read.'
                  : ''}
                {basemapPaths && basemap?.source
                  ? ` Roads, rivers and lakes: ${basemap.source}, drawn in this map's own projection.`
                  : ''}
                {amenityParks.length > 0 || amenityTrails.length > 0
                  ? ' Park outlines from recorded park boundaries in public.boundaries (geo_type=park). Trail lines from public.trail_lines (USFS / BPRD / BLM / OPRD). Missing official geometry is omitted.'
                  : ''}
              </p>
            </details>
          ) : null}
        </div>}
      </div>
    </section>
  )
}
